// v49.0 — LABOURCORE_API entry point.
// Exposes the same surface as the v48.311 IIFE at lines 10508-11290 so
// every existing call site (e.g. window.LABOURCORE_API.placements.list)
// continues to work once migrated. Also re-exposes on window for any
// straggler vanilla JS / DevTools console diagnostic that depends on it.
//
// Module breakdown:
//   supabase-client.ts     — the createClient singleton
//   types.ts               — TS type aliases for every table
//   table.ts               — generic CRUD factory
//   auth.ts                — sign-in/up/out + onAuthStateChange
//   This file              — composes the namespaces + table-specific extensions

import { supabase, SUPABASE_READY } from "./supabase-client";
import { auth } from "./auth";
import { table } from "./table";
import { lcAudit } from "../audit";
import type {
  Profile, Company, Operative, Requirement, Placement,
  PlacementRating, FeeEvent, PlatformInvoice, DailyLog, CoordCrewTemplate, ComplianceDocument,
  PlacementMessage, PlacementNotification, PartnerOnboarding,
  ContractOffer, ComplianceRequest, Dispute, DisputeStatus, DisputeSide, DisputeComment,
  PlacementMilestone, Invoice, InvoiceStatus,
  PartnerOrigination,
  DealClarification,
  TenderClarification,
  TenderAmendment,
  TenderInvitation,
  TenderDocument,
  BidPackDocument,
  SavedTenderSearch,
  BidOpportunity,
  BidPack,
  FuturesBooking,
  PartnerOffer,
} from "./types";

/* v48.274 — active-tenant pointer. Many table extensions resolve their
   tenant from this rather than the caller passing a company_id every
   call. Set on signin via setActiveCompanyId; read by hooks. */
let _activeCompanyId: string | null = null;
function setActiveCompanyId(id: string | null) { _activeCompanyId = id; }
function getActiveCompanyId(): string | null   { return _activeCompanyId; }

/* v48.274 — atomic company creation RPC. */
const companies = Object.assign(table<Company>("companies"), {
  async createForUser(name: string, kind: Company["kind"]): Promise<string | null> {
    if (!SUPABASE_READY) return null;
    const { data, error } = await supabase.rpc("create_company_for_user", {
      p_name: name,
      p_kind: kind,
    });
    if (error) { console.warn("[LABOURCORE_API] companies.createForUser:", error); return null; }
    const id = (data as string) || null;
    if (id) setActiveCompanyId(id);
    lcAudit({ event_type: "company.created", ui_string: name, metadata: { company_id: id, kind } });
    return id;
  },
  async findById(id: string) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase.from("companies").select("*").eq("id", id).maybeSingle();
    if (error) { console.warn("[LABOURCORE_API] companies.findById:", error); return null; }
    return data as Company | null;
  },
  /* v49.58 — Public partner profile (anonymous). Returns the
     aggregate reputation bundle for the slug, or null if not found
     / not public. Bypasses normal RLS via SECURITY DEFINER. */
  async getPublicProfile(slug: string) {
    if (!SUPABASE_READY || !slug) return null;
    const { data, error } = await supabase.rpc("get_public_partner_profile", { p_slug: slug });
    if (error) { console.warn("[LABOURCORE_API] companies.getPublicProfile:", error); return null; }
    return data as Record<string, unknown> | null;
  },
  async setPublicProfileVisibility(companyId: string, visible: boolean) {
    if (!SUPABASE_READY || !companyId) return false;
    const { error } = await supabase.rpc("set_public_profile_visibility", {
      p_company_id: companyId,
      p_visible:    visible,
    });
    if (error) { console.warn("[LABOURCORE_API] companies.setPublicProfileVisibility:", error); return false; }
    lcAudit({ event_type: visible ? "public_profile.enabled" : "public_profile.disabled", metadata: { company_id: companyId } });
    return true;
  },
});

const memberships = Object.assign(table("memberships"), {
  async listForUser(userId: string) {
    if (!SUPABASE_READY) return null;
    const { data, error } = await supabase
      .from("memberships")
      .select("*, company:company_id(name, kind, reliability_score, performance_score, partner_progression)")
      .eq("user_id", userId);
    if (error) { console.warn("[LABOURCORE_API] memberships.listForUser:", error); return null; }
    return data || [];
  },
});

/* v48.288–298 — requirements with custom helpers (listOpen + matchOperatives RPC). */
const requirements = Object.assign(table<Requirement>("requirements"), {
  async listOpen(filter?: { trade?: string; urgent?: boolean }) {
    if (!SUPABASE_READY) return null;
    let q = supabase
      .from("requirements")
      .select("*, coordinator_company_id, creator:created_by_user_id(name,email,company)")
      .eq("status", "open")
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(200);
    if (filter?.trade)  q = q.eq("trade", filter.trade);
    if (filter?.urgent) q = q.eq("urgent", true);
    const { data, error } = await q;
    if (error) { console.warn("[LABOURCORE_API] requirements.listOpen:", error); return null; }
    return data || [];
  },
  async matchOperatives(reqId: string) {
    if (!SUPABASE_READY || !reqId) return null;
    const { data, error } = await supabase.rpc("rank_operatives_for_requirement", { p_req_id: reqId });
    if (error) { console.warn("[LABOURCORE_API] requirements.matchOperatives:", error); return null; }
    return data || [];
  },
  /* v50.99 — dedicated create with ok/reason union so the wizard can
     surface Postgres errors inline. Generic .insert from the table
     wrapper stays for legacy callers; this is the modern path.
     Auth-session lookup mirrors the v50.98 booking fix to populate
     created_by_user_id automatically. */
  async create(args: Partial<Requirement> & { trade: string; headcount: number; status?: string }):
    Promise<{ ok: true; data: Requirement } | { ok: false; reason: string }>
  {
    if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
    const sessionRes = await supabase.auth.getSession();
    const createdBy = sessionRes?.data?.session?.user?.id;
    if (!createdBy) return { ok: false, reason: "Not signed in" };
    const row = { created_by_user_id: createdBy, status: "open" as const, ...args };
    const { data, error } = await supabase
      .from("requirements")
      .insert(row)
      .select("*")
      .maybeSingle();
    if (error) {
      console.warn("[LABOURCORE_API] requirements.create:", error);
      const reason = [error.message, error.code && `(${error.code})`, error.hint, error.details]
        .filter(Boolean).join(" · ");
      return { ok: false, reason: reason || "Unknown error" };
    }
    if (!data) return { ok: false, reason: "Insert succeeded but no row returned (RLS may block read-back)" };
    lcAudit({
      event_type: "requirement.created",
      ui_string:  `${args.trade} × ${args.headcount}${args.location ? ` · ${args.location}` : ""}`,
      metadata:   {
        requirement_id: (data as Requirement).id,
        trade:          args.trade,
        headcount:      args.headcount,
        location:       args.location ?? null,
        start_date:     args.start_date ?? null,
      },
    });
    return { ok: true, data: data as Requirement };
  },
});

/* v48.289–298 — placements: full marketplace primitive. */
const placements = Object.assign(table<Placement>("placements"), {
  async invite(reqId: string, operativeId: string, message?: string | null) {
    if (!SUPABASE_READY || !reqId || !operativeId) return null;
    const { data, error } = await supabase.rpc("invite_operative_to_requirement", {
      p_req_id:       reqId,
      p_operative_id: operativeId,
      p_message:      (message && message.trim()) || null,
    });
    if (error) { console.warn("[LABOURCORE_API] placements.invite:", error); return null; }
    lcAudit({
      event_type: "placement.invited",
      ui_string:  "Invited operative to requirement",
      metadata:   { requirement_id: reqId, operative_id: operativeId, placement_id: data },
    });
    return data || null;
  },
  async listByRequirement(reqId: string) {
    if (!SUPABASE_READY || !reqId) return null;
    const { data, error } = await supabase
      .from("placements")
      .select("*, operative:operative_id(full_name,trade,day_rate_target,reliability_pct), partner_co:partner_company_id(name)")
      .eq("requirement_id", reqId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) { console.warn("[LABOURCORE_API] placements.listByRequirement:", error); return null; }
    return data || [];
  },
  async findById(id: string) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase
      .from("placements")
      .select("*, operative:operative_id(full_name,trade,day_rate_target,reliability_pct,quality_score), requirement:requirement_id(trade,headcount,start_date,duration_weeks,urgent,location,day_rate_target,id), partner_co:partner_company_id(id,name,reliability_score,performance_score,partner_progression), coord_co:coordinator_company_id(id,name)")
      .eq("id", id)
      .is("deleted_at", null)
      .maybeSingle();
    if (error) { console.warn("[LABOURCORE_API] placements.findById:", error); return null; }
    return data;
  },
  async listForCompany(opts?: { companyId?: string; side?: "partner" | "coordinator" }) {
    if (!SUPABASE_READY) return null;
    const co = opts?.companyId || getActiveCompanyId();
    if (!co) return null;
    const side = opts?.side || "partner";
    const col  = side === "coordinator" ? "coordinator_company_id" : "partner_company_id";
    const { data, error } = await supabase
      .from("placements")
      .select("*, operative:operative_id(full_name,trade), requirement:requirement_id(trade,headcount,start_date,urgent,location)")
      .eq(col, co)
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) { console.warn("[LABOURCORE_API] placements.listForCompany:", error); return null; }
    return data || [];
  },
  /* v50.173 — accept_proposal RPC wrapper. Replaces the v49.x client-side
     UPDATE that stamped stage='invited' with no terms. The RPC mints the
     placement at stage='contracted' with agreed_day_rate + ir35_status +
     agreed_at captured, plus optional requirement-side start_date /
     duration_weeks edits. Returns the updated placement row or null on
     failure (caller routes the error through humanError). */
  async accept(
    placementId: string,
    payload: {
      agreedDayRate:  number;
      ir35Status?:    string | null;
      startDate?:     string | null;
      durationWeeks?: number | null;
    },
  ): Promise<Placement | null> {
    if (!SUPABASE_READY || !placementId) return null;
    const { data, error } = await supabase.rpc("accept_proposal", {
      p_placement_id:    placementId,
      p_agreed_day_rate: payload.agreedDayRate,
      p_ir35_status:     payload.ir35Status ?? null,
      p_start_date:      payload.startDate ?? null,
      p_duration_weeks:  payload.durationWeeks ?? null,
    });
    if (error) {
      console.warn("[LABOURCORE_API] placements.accept:", error);
      /* Throw so the caller's catch can route through humanError(). The
         error object carries the Postgres message verbatim and that's
         what the user needs to see for stage-gate / not-a-member /
         positive-rate violations. */
      throw error;
    }
    lcAudit({
      event_type: "placement.accepted",
      ui_string:  "Accepted proposal via ContractReview buy-box",
      metadata:   { placement_id: placementId, agreed_day_rate: payload.agreedDayRate, ir35_status: payload.ir35Status || null },
    });
    return (data as Placement | null) || null;
  },
});

/* v48.294 — placement_ratings with rate() helper. */
const placement_ratings = Object.assign(table<PlacementRating>("placement_ratings"), {
  async listForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return null;
    const { data, error } = await supabase
      .from("placement_ratings")
      .select("*, rater:rated_by_user_id(name,email,company)")
      .eq("placement_id", placementId)
      .order("created_at", { ascending: false });
    if (error) { console.warn("[LABOURCORE_API] placement_ratings.listForPlacement:", error); return null; }
    return data || [];
  },
  async rate(args: {
    placement: Placement;
    reliability?: number;
    quality?: number;
    comms?: number;
    safety?: number;
    rehire?: number;
    payment_timeliness?: number;
    site_conditions?: number;
    comment?: string | null;
  }) {
    if (!SUPABASE_READY || !args?.placement) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const activeCo = getActiveCompanyId();
    if (!userId || !activeCo) return null;
    const p = args.placement;
    let raterSide: "coordinator" | "partner" | null = null;
    let ratedCompanyId: string | null = null;
    if (p.coordinator_company_id === activeCo) { raterSide = "coordinator"; ratedCompanyId = p.partner_company_id; }
    else if (p.partner_company_id === activeCo) { raterSide = "partner";     ratedCompanyId = p.coordinator_company_id; }
    if (!raterSide || !ratedCompanyId) return null;
    const row = {
      placement_id:     p.id,
      rated_by_user_id: userId,
      rater_side:       raterSide,
      rated_company_id: ratedCompanyId,
      reliability:      args.reliability || null,
      quality:          args.quality     || null,
      comms:            args.comms       || null,
      safety:             args.safety             || null,
      rehire:             args.rehire             || null,
      payment_timeliness: args.payment_timeliness || null,
      site_conditions:    args.site_conditions    || null,
      comment:          (args.comment && args.comment.trim()) || null,
    };
    const { data, error } = await supabase.from("placement_ratings").insert(row).select().single();
    if (error) { console.warn("[LABOURCORE_API] placement_ratings.rate:", error); return null; }
    lcAudit({
      event_type: "rating.submitted",
      ui_string:  `${raterSide} rated counterparty`,
      metadata:   {
        placement_id: p.id,
        rated_company_id: ratedCompanyId,
        rater_side: raterSide,
        reliability: args.reliability ?? null,
        quality:     args.quality     ?? null,
        comms:       args.comms       ?? null,
      },
    });
    return data as PlacementRating;
  },
});

/* v48.292 — daily_logs with confirm() helper.
   v50.238 — submit() wraps the bare table insert with an audit row
   so partner-side submissions feed the /audit profile the same way
   coord-side confirms do. The submit modal in DailyLogsPanel used
   to call .insert() directly which skipped the audit trail, so the
   "Logs to submit" → submit flow looked complete in-app but left
   no fingerprint on the audit page. */
const daily_logs = Object.assign(table<DailyLog>("daily_logs"), {
  async submit(input: {
    placement_id:         string;
    log_date:             string;
    hours:                number;
    notes?:               string;
    submitted_by_user_id?: string;
  }) {
    if (!SUPABASE_READY || !input.placement_id) return null;
    /* v50.264 — column on the live table is hours_worked. Pre-v50.264
       this insert was writing through a non-existent `hours` key and
       silently dropping the row. Callers still pass input.hours for
       backward-compat; we translate at the boundary. */
    const { data, error } = await supabase
      .from("daily_logs")
      .insert({
        placement_id:         input.placement_id,
        log_date:             input.log_date,
        hours_worked:         input.hours,
        notes:                input.notes ?? null,
        submitted_by_user_id: input.submitted_by_user_id ?? null,
        submitted_at:         new Date().toISOString(),
      })
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] daily_logs.submit:", error); return null; }
    lcAudit({
      event_type: "daily_log.submitted",
      ui_string:  `Submitted ${input.hours}h log for ${input.log_date}`,
      metadata:   { log_id: data?.id, placement_id: input.placement_id, log_date: input.log_date, hours: input.hours },
    });
    return data as DailyLog;
  },
  async listForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return null;
    const { data, error } = await supabase
      .from("daily_logs")
      .select("*, submitter:submitted_by_user_id(name,email), confirmer:confirmed_by_user_id(name,email)")
      .eq("placement_id", placementId)
      .order("log_date", { ascending: false })
      .limit(200);
    if (error) { console.warn("[LABOURCORE_API] daily_logs.listForPlacement:", error); return null; }
    return data || [];
  },
  async confirm(logId: string) {
    if (!SUPABASE_READY || !logId) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const nowIso = new Date().toISOString();
    const { data: updated, error } = await supabase
      .from("daily_logs")
      .update({ confirmed_by_user_id: userId, confirmed_at: nowIso })
      .eq("id", logId)
      .select("*, placement_id")
      .single();
    if (error) { console.warn("[LABOURCORE_API] daily_logs.confirm:", error); return null; }
    lcAudit({
      event_type: "daily_log.confirmed",
      ui_string:  "Confirmed daily log",
      metadata:   { log_id: logId, placement_id: updated?.placement_id },
    });
    /* First confirm auto-bumps placement to active + stamps onsite_at. */
    try {
      if (updated?.placement_id) {
        const { data: pl } = await supabase.from("placements").select("id, stage, onsite_at").eq("id", updated.placement_id).single();
        if (pl && pl.stage !== "active" && pl.stage !== "completed" && !pl.onsite_at) {
          await supabase.from("placements").update({ stage: "active", onsite_at: nowIso }).eq("id", updated.placement_id);
        }
      }
    } catch(_) {}
    return updated;
  },
});

/* v48.299 — coord_crew_templates with engagedOperatives() + bulkInvite(). */
const coord_crew_templates = Object.assign(table<CoordCrewTemplate>("coord_crew_templates"), {
  async listForCoord() {
    const co = getActiveCompanyId();
    if (!SUPABASE_READY || !co) return null;
    return await coord_crew_templates.list({ coordinator_company_id: co } as Partial<CoordCrewTemplate>);
  },
  async engagedOperatives() {
    const co = getActiveCompanyId();
    if (!SUPABASE_READY || !co) return null;
    const { data, error } = await supabase.rpc("operatives_engaged_by_coord", { p_coord_co: co });
    if (error) { console.warn("[LABOURCORE_API] coord_crew_templates.engagedOperatives:", error); return null; }
    return data || [];
  },
  async bulkInvite(templateId: string, reqId: string) {
    if (!SUPABASE_READY || !templateId || !reqId) return [];
    const { data: tpl } = await supabase.from("coord_crew_templates").select("operative_ids").eq("id", templateId).single();
    const ids: string[] = Array.isArray(tpl?.operative_ids) ? tpl!.operative_ids : [];
    if (ids.length === 0) return [];
    const results = await Promise.all(ids.map(async (opId) => {
      try { return { operativeId: opId, placementId: await placements.invite(reqId, opId) }; }
      catch (err) { return { operativeId: opId, error: String((err as Error).message || err) }; }
    }));
    return results;
  },
});

/* v48.300/302 — fee_events with lifecycle mutators. */
const fee_events = Object.assign(table<FeeEvent>("fee_events"), {
  async listForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return null;
    const { data, error } = await supabase
      .from("fee_events").select("*").eq("placement_id", placementId).limit(1).maybeSingle();
    if (error) { console.warn("[LABOURCORE_API] fee_events.listForPlacement:", error); return null; }
    return data || null;
  },
  async markInvoiced(id: string, notes?: string) {
    if (!SUPABASE_READY || !id) return null;
    const patch: Partial<FeeEvent> = { status: "invoiced", invoiced_at: new Date().toISOString() };
    if (notes && notes.trim()) patch.notes = notes.trim();
    const { data, error } = await supabase.from("fee_events").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] fee_events.markInvoiced:", error); return null; }
    lcAudit({ event_type: "fee.invoiced", metadata: { fee_event_id: id, notes: notes || null } });
    return data as FeeEvent;
  },
  async markPaid(id: string, notes?: string) {
    if (!SUPABASE_READY || !id) return null;
    const patch: Partial<FeeEvent> = { status: "paid", paid_at: new Date().toISOString() };
    if (notes && notes.trim()) patch.notes = notes.trim();
    const { data, error } = await supabase.from("fee_events").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] fee_events.markPaid:", error); return null; }
    lcAudit({ event_type: "fee.paid", metadata: { fee_event_id: id, notes: notes || null } });
    return data as FeeEvent;
  },
  async markWaived(id: string, notes?: string) {
    if (!SUPABASE_READY || !id) return null;
    const patch: Partial<FeeEvent> = { status: "waived" };
    if (notes && notes.trim()) patch.notes = notes.trim();
    const { data, error } = await supabase.from("fee_events").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] fee_events.markWaived:", error); return null; }
    lcAudit({ event_type: "fee.waived", metadata: { fee_event_id: id, notes: notes || null } });
    return data as FeeEvent;
  },
  /* v49.48 — Stripe checkout for the platform introduction fee. Hits
     the worker endpoint with the user's JWT; server creates the
     session, records its id, and returns the hosted checkout URL.
     Caller is expected to redirect via window.location.assign. */
  async startStripeCheckout(id: string): Promise<{ url: string; sessionId: string } | null> {
    if (!SUPABASE_READY || !id) return null;
    const sessionRes = await supabase.auth.getSession();
    const accessToken = sessionRes?.data?.session?.access_token;
    if (!accessToken) { console.warn("[fee_events.startStripeCheckout] no session"); return null; }
    try {
      const r = await fetch("/api/create-fee-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ fee_event_id: id }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        console.warn("[fee_events.startStripeCheckout]", r.status, data);
        return null;
      }
      lcAudit({ event_type: "fee.checkout_started", metadata: { fee_event_id: id, session_id: data?.sessionId } });
      return data as { url: string; sessionId: string };
    } catch (e) {
      console.warn("[fee_events.startStripeCheckout] threw:", e);
      return null;
    }
  },
});

/* v50.320 — platform_invoices (migration-135). LabourCore → coordinator
   direct fee invoices, issued when labour hits site, net-30. Read-only
   from the client (RLS allows coord members + admins to SELECT); all
   writes go via SECURITY DEFINER RPCs. */
const platform_invoices = Object.assign(table<PlatformInvoice>("platform_invoices"), {
  async listForCompany(companyId?: string) {
    if (!SUPABASE_READY) return null;
    const co = companyId || getActiveCompanyId();
    if (!co) return null;
    const { data, error } = await supabase
      .from("platform_invoices").select("*")
      .eq("coordinator_company_id", co)
      .order("issue_date", { ascending: false })
      .limit(200);
    if (error) { console.warn("[LABOURCORE_API] platform_invoices.listForCompany:", error); return null; }
    return (data as PlatformInvoice[]) || [];
  },
  /* Line items: every fee event rolled into this invoice. */
  async listFeeEvents(invoiceId: string) {
    if (!SUPABASE_READY || !invoiceId) return null;
    const { data, error } = await supabase
      .from("fee_events").select("*")
      .eq("platform_invoice_id", invoiceId)
      .order("triggered_at", { ascending: true });
    if (error) { console.warn("[LABOURCORE_API] platform_invoices.listFeeEvents:", error); return null; }
    return (data as FeeEvent[]) || [];
  },
  /* Admin back-office: full queue, optionally filtered by status. */
  async listAll(status?: PlatformInvoice["status"]) {
    if (!SUPABASE_READY) return null;
    let q = supabase
      .from("platform_invoices")
      .select("*, coordinator:coordinator_company_id(name)")
      .order("issue_date", { ascending: false })
      .limit(300);
    if (status) q = q.eq("status", status);
    const { data, error } = await q;
    if (error) { console.warn("[LABOURCORE_API] platform_invoices.listAll:", error); return null; }
    return (data as (PlatformInvoice & { coordinator?: { name?: string } | null })[]) || [];
  },
  /* Admin reconciles an off-platform bank transfer. */
  async adminMarkPaid(invoiceId: string, notes?: string) {
    if (!SUPABASE_READY || !invoiceId) return false;
    const { error } = await supabase.rpc("admin_mark_platform_invoice_paid", {
      p_invoice_id: invoiceId,
      p_notes: notes && notes.trim() ? notes.trim() : null,
    });
    if (error) { console.warn("[LABOURCORE_API] platform_invoices.adminMarkPaid:", error); return false; }
    lcAudit({ event_type: "platform_invoice.paid", metadata: { invoice_id: invoiceId, via: "bank_transfer" } });
    return true;
  },
  async adminVoid(invoiceId: string, reason: string) {
    if (!SUPABASE_READY || !invoiceId || !reason?.trim()) return false;
    const { error } = await supabase.rpc("admin_void_platform_invoice", {
      p_invoice_id: invoiceId,
      p_reason: reason.trim(),
    });
    if (error) { console.warn("[LABOURCORE_API] platform_invoices.adminVoid:", error); return false; }
    lcAudit({ event_type: "platform_invoice.voided", metadata: { invoice_id: invoiceId } });
    return true;
  },
  /* Optional Stripe pay-now. Mirrors fee_events.startStripeCheckout. */
  async startStripeCheckout(invoiceId: string): Promise<{ url: string; sessionId: string } | null> {
    if (!SUPABASE_READY || !invoiceId) return null;
    const sessionRes = await supabase.auth.getSession();
    const accessToken = sessionRes?.data?.session?.access_token;
    if (!accessToken) { console.warn("[platform_invoices.startStripeCheckout] no session"); return null; }
    try {
      const r = await fetch("/api/create-platform-invoice-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ invoice_id: invoiceId }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        console.warn("[platform_invoices.startStripeCheckout]", r.status, data);
        return null;
      }
      lcAudit({ event_type: "platform_invoice.checkout_started", metadata: { invoice_id: invoiceId, session_id: data?.sessionId } });
      return data as { url: string; sessionId: string };
    } catch (e) {
      console.warn("[platform_invoices.startStripeCheckout] threw:", e);
      return null;
    }
  },
});

/* v48.279/285/286 — compliance_documents with upload helpers.
   Storage path convention: <company_id>/<operative_id|firm-level>/<timestamp>-<filename>
   enforced server-side by the compliance-docs bucket RLS (migration-001).
   View access via signed URL (60-minute expiry).
   v49.5 port: full upload + listForCompany + signedUrl. */
const compliance_documents = Object.assign(table<ComplianceDocument>("compliance_documents"), {
  async listForOperative(operativeId: string) {
    if (!SUPABASE_READY || !operativeId) return null;
    const { data, error } = await supabase
      .from("compliance_documents").select("*").eq("operative_id", operativeId).is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) { console.warn("[LABOURCORE_API] compliance_documents.listForOperative:", error); return null; }
    return data || [];
  },
  async listForCompany(opts?: { companyId?: string }) {
    if (!SUPABASE_READY) return null;
    const co = opts?.companyId || getActiveCompanyId();
    if (!co) return null;
    const { data, error } = await supabase
      .from("compliance_documents")
      .select("*, operative:operative_id(full_name, trade)")
      .eq("company_id", co)
      .is("deleted_at", null)
      .order("expires_at", { ascending: true, nullsFirst: false });
    if (error) { console.warn("[LABOURCORE_API] compliance_documents.listForCompany:", error); return null; }
    return data || [];
  },
  /* Upload a file to the compliance-docs bucket + insert the metadata row.
     `opts.operativeId` optional — when omitted, treated as a firm-level
     document (path uses "firm-level" segment). Returns the metadata row
     on success, null on any failure (errors logged). */
  async upload(file: File, opts: {
    certLabel?:   string;
    certKind?:    string;
    expiresAt?:   string;   /* YYYY-MM-DD */
    operativeId?: string;
  }) {
    if (!SUPABASE_READY) return null;
    const co = getActiveCompanyId();
    if (!co) { console.warn("[compliance.upload] no active tenant"); return null; }
    const operativeSeg = opts.operativeId || "firm-level";
    const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
    const path     = `${co}/${operativeSeg}/${Date.now()}-${safeName}`;
    try {
      const { error: upErr } = await supabase.storage
        .from("compliance-docs")
        .upload(path, file, { upsert: false, cacheControl: "3600" });
      if (upErr) { console.warn("[compliance.upload] storage:", upErr); return null; }
      /* v50.329 — Defence P3: SHA-256 fingerprint of the exact bytes stored,
         per the playbook's hash-every-document rule. Non-fatal on failure
         (column is nullable; historical rows are null too). */
      let sha256: string | null = null;
      try {
        const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
        sha256 = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
      } catch { /* hash unavailable — proceed without */ }
      const sessionRes = await supabase.auth.getSession();
      const userId = sessionRes?.data?.session?.user?.id || null;
      const { data: row, error: insErr } = await supabase
        .from("compliance_documents")
        .insert({
          company_id:    co,
          operative_id:  opts.operativeId || null,
          file_name:     file.name,
          file_path:     path,
          file_size:     file.size,
          mime_type:     file.type || null,
          cert_label:    opts.certLabel || null,
          cert_kind:     opts.certKind  || null,
          expires_at:    opts.expiresAt || null,
          uploaded_by:   userId,
          sha256,
        })
        .select()
        .single();
      if (insErr) { console.warn("[compliance.upload] insert:", insErr); return null; }
      lcAudit({
        event_type: "compliance.uploaded",
        ui_string:  opts.certLabel || opts.certKind || file.name,
        metadata:   {
          document_id:  (row as ComplianceDocument).id,
          company_id:   co,
          operative_id: opts.operativeId || null,
          cert_kind:    opts.certKind || null,
          expires_at:   opts.expiresAt || null,
        },
      });
      return row as ComplianceDocument;
    } catch (e) {
      console.warn("[compliance.upload] threw:", e);
      return null;
    }
  },
  async signedUrl(filePath: string, ttlSeconds = 3600) {
    if (!SUPABASE_READY || !filePath) return null;
    const { data, error } = await supabase.storage.from("compliance-docs").createSignedUrl(filePath, ttlSeconds);
    if (error) { console.warn("[compliance.signedUrl]:", error); return null; }
    return data?.signedUrl || null;
  },
  /* v50.120 — AI-verify a compliance document by classifying it with Claude.
     Calls the /api/compliance-verify-doc Pages Function which auth-checks
     the user's JWT, RLS-reads the doc, downloads via signed URL, calls the
     Anthropic API, then RLS-writes the result back to the same row.
     Resolves to the verification payload on success, throws on failure. */
  async aiVerify(documentId: string): Promise<{
    document_id:    string;
    ai_verified_at: string;
    ai_cert_kind:   string | null;
    ai_expires_at:  string | null;
    ai_confidence:  number | null;
    ai_findings:    { holder_name?: string | null; cert_number?: string | null; issuer?: string | null; notes?: string | null };
    ai_flags:       string[];
  }> {
    if (!SUPABASE_READY) throw new Error("Supabase not initialised");
    const sessionRes = await supabase.auth.getSession();
    const token = sessionRes?.data?.session?.access_token;
    if (!token) throw new Error("Not signed in");
    const resp = await fetch("/api/compliance-verify-doc", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ document_id: documentId }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error((json && json.error) || `Verify failed (${resp.status})`);
    try { window.dispatchEvent(new CustomEvent("lc-compliance-changed", { detail: { id: documentId, source: "ai-verify" } })); } catch { /* ignore */ }
    lcAudit({
      event_type: "compliance.ai_verified",
      ui_string:  json.ai_cert_kind || "unclassifiable",
      metadata:   {
        document_id:   documentId,
        ai_cert_kind:  json.ai_cert_kind,
        ai_expires_at: json.ai_expires_at,
        ai_confidence: json.ai_confidence,
        ai_flags:      json.ai_flags,
      },
    });
    return json;
  },
});

/* v48.301/303 — insights namespace: fetchPortfolio + summary + subscribe. */
const insights = {
  async fetchPortfolio() {
    if (!SUPABASE_READY) return null;
    const { data, error } = await supabase
      .from("placements")
      .select("id, requirement_id, partner_company_id, coordinator_company_id, operative_id, stage, agreed_day_rate, onsite_at, completed_at, created_at, requirement:requirement_id(trade,headcount,day_rate_target,duration_weeks,start_date,location,created_at), partner_co:partner_company_id(name,reliability_score,performance_score,partner_progression), operative:operative_id(full_name,trade,quality_score,reliability_pct), ratings:placement_ratings(quality,reliability,comms,rater_side), fee:fee_events(estimated_fee_amount,fee_pct,headcount_tier,triggered_at)")
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(1000);
    if (error) { console.warn("[LABOURCORE_API] insights.fetchPortfolio:", error); return null; }
    return data || [];
  },
  subscribe(handler: () => void) {
    if (!SUPABASE_READY) return { unsubscribe: () => {} };
    const channels = [
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      supabase.channel("insights:placements").on("postgres_changes" as any, { event: "*", schema: "public", table: "placements" }, handler).subscribe(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      supabase.channel("insights:ratings").on("postgres_changes" as any, { event: "*", schema: "public", table: "placement_ratings" }, handler).subscribe(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      supabase.channel("insights:fee_events").on("postgres_changes" as any, { event: "*", schema: "public", table: "fee_events" }, handler).subscribe(),
    ];
    return { unsubscribe: () => channels.forEach(c => { try { supabase.removeChannel(c); } catch(_) {} }) };
  },
  async summary(digest: Record<string, unknown>, role?: string | null) {
    if (!SUPABASE_READY) return null;
    try {
      const sessionRes = await supabase.auth.getSession();
      const accessToken = sessionRes?.data?.session?.access_token || null;
      if (!accessToken) return null;
      const resp = await fetch("/api/insights-summary", {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ digest, role: role || null }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) { console.warn("[insights.summary]", resp.status, data); return null; }
      return data;
    } catch (e) {
      console.warn("[insights.summary] threw:", e);
      return null;
    }
  },
};

/* v49.12 — placement_messages namespace.
   Per-placement chat between coord + partner. send() figures out the
   sender_side from the active company vs the placement's coord/partner
   company. markRead() flips read_by_other_at on every visible row that
   wasn't sent by the current user (RLS policy enforces sender_user_id
   <> auth.uid() anyway). */
const placement_messages = Object.assign(table<PlacementMessage>("placement_messages"), {
  async listForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return null;
    /* v50.343 — dropped vestigial sender embed: FK targets auth.users which
       has email but no name column → 400. The result is never read for
       sender.name/email anywhere in the codebase. */
    const { data, error } = await supabase
      .from("placement_messages")
      .select("*")
      .eq("placement_id", placementId)
      .order("created_at", { ascending: true })
      .limit(500);
    if (error) { console.warn("[LABOURCORE_API] placement_messages.listForPlacement:", error); return null; }
    return data || [];
  },
  async send(placement: Placement, body: string) {
    if (!SUPABASE_READY || !placement || !body.trim()) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const activeCo = getActiveCompanyId();
    if (!userId || !activeCo) return null;
    let senderSide: "coordinator" | "partner" | null = null;
    if (placement.coordinator_company_id === activeCo) senderSide = "coordinator";
    else if (placement.partner_company_id === activeCo) senderSide = "partner";
    if (!senderSide) return null;
    const { data, error } = await supabase.from("placement_messages").insert({
      placement_id:   placement.id,
      sender_user_id: userId,
      sender_side:    senderSide,
      body:           body.trim(),
    }).select().single();
    if (error) { console.warn("[LABOURCORE_API] placement_messages.send:", error); return null; }
    return data as PlacementMessage;
  },
  async markReadForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return 0;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    if (!userId) return 0;
    const { data, error } = await supabase
      .from("placement_messages")
      .update({ read_by_other_at: new Date().toISOString() })
      .eq("placement_id", placementId)
      .neq("sender_user_id", userId)
      .is("read_by_other_at", null)
      .select("id");
    if (error) { console.warn("[LABOURCORE_API] placement_messages.markReadForPlacement:", error); return 0; }
    return data?.length || 0;
  },
});

/* v49.12 — placement_notifications namespace.
   Per-user notifications fired by server triggers on message inserts,
   stage changes, log activity and ratings. unreadCount + subscribe drive
   the topbar bell; listForUser + markRead drive the /notifications
   page. */
const placement_notifications = Object.assign(table<PlacementNotification>("placement_notifications"), {
  async listForUser(opts?: { onlyUnread?: boolean; limit?: number }) {
    if (!SUPABASE_READY) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    if (!userId) return null;
    let q = supabase
      .from("placement_notifications")
      .select("*, placement:placement_id(id, requirement_id, partner_company_id, coordinator_company_id, operative:operative_id(full_name,trade))")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(opts?.limit ?? 100);
    if (opts?.onlyUnread) q = q.is("read_at", null);
    const { data, error } = await q;
    if (error) { console.warn("[LABOURCORE_API] placement_notifications.listForUser:", error); return null; }
    return data || [];
  },
  async unreadCount() {
    if (!SUPABASE_READY) return 0;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    if (!userId) return 0;
    const { count, error } = await supabase
      .from("placement_notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .is("read_at", null);
    if (error) { console.warn("[LABOURCORE_API] placement_notifications.unreadCount:", error); return 0; }
    return count ?? 0;
  },
  async markRead(id: string) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase
      .from("placement_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] placement_notifications.markRead:", error); return null; }
    return data as PlacementNotification;
  },
  /* v50.45 — optional kind filter so callers can mark-read scoped to
     the active NotificationsPage chip filter. Omit kind to mark every
     unread row (original behaviour). */
  async markAllRead(kind?: string) {
    if (!SUPABASE_READY) return 0;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    if (!userId) return 0;
    let q = supabase
      .from("placement_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", userId)
      .is("read_at", null);
    if (kind) q = q.eq("kind", kind);
    const { data, error } = await q.select("id");
    if (error) { console.warn("[LABOURCORE_API] placement_notifications.markAllRead:", error); return 0; }
    return data?.length || 0;
  },
});

/* v49.26 — contract_offers (4-step offer wizard, partner accept/reject).
   v48.6 created the base table; migration-023 added placement_id +
   partner_company_id + coordinator_company_id for the multi-tenant model. */
const contract_offers = Object.assign(table<ContractOffer>("contract_offers"), {
  async listForPartner(partnerCompanyId: string) {
    if (!SUPABASE_READY || !partnerCompanyId) return null;
    const { data, error } = await supabase
      .from("contract_offers")
      .select("*")
      .eq("partner_company_id", partnerCompanyId)
      .order("offered_at", { ascending: false })
      .limit(100);
    if (error) { console.warn("[LABOURCORE_API] contract_offers.listForPartner:", error); return null; }
    return (data as ContractOffer[]) || [];
  },
  async listMineWithPartner(partnerCompanyId: string) {
    if (!SUPABASE_READY || !partnerCompanyId) return null;
    const co = getActiveCompanyId();
    if (!co) return null;
    const { data, error } = await supabase
      .from("contract_offers")
      .select("*")
      .eq("coordinator_company_id", co)
      .eq("partner_company_id", partnerCompanyId)
      .order("offered_at", { ascending: false })
      .limit(100);
    if (error) { console.warn("[LABOURCORE_API] contract_offers.listMineWithPartner:", error); return null; }
    return (data as ContractOffer[]) || [];
  },
  async create(args: {
    partnerCompanyId:        string;
    partnerName:             string;
    placementId?:            string | null;
    contractTerms:           Record<string, unknown>;
    legalFrameworkVersion?:  string;
    contractHash:            string;
    uiStringShown:           string;
    idempotencyKey?:         string;
  }) {
    if (!SUPABASE_READY) return null;
    const co = getActiveCompanyId();
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id;
    const userEmail = sessionRes?.data?.session?.user?.email;
    if (!co || !userId) return null;
    const row = {
      coordinator_company_id:  co,
      partner_company_id:      args.partnerCompanyId,
      placement_id:            args.placementId || null,
      offered_by:              userId,
      offered_by_email:        userEmail || "",
      offered_to_partner_name: args.partnerName,
      contract_terms:          args.contractTerms,
      legal_framework_version: args.legalFrameworkVersion || "v1.1",
      contract_hash:           args.contractHash,
      ui_string_shown:         args.uiStringShown,
      status:                  "sent" as const,
      offered_at:              new Date().toISOString(),
      idempotency_key:         args.idempotencyKey || `co_${userId}_${args.partnerCompanyId}_${Date.now()}`,
    };
    const { data, error } = await supabase.from("contract_offers").insert(row).select().single();
    if (error) { console.warn("[LABOURCORE_API] contract_offers.create:", error); return null; }
    /* v50.280 — describe the offer in the audit row (value + order
       ref + counter notes when present) so AuditLog reads as a
       description, not just an ID. */
    const ctCreate     = args.contractTerms as { contract_value_pence?: number; order_ref?: string; counter_notes?: string | null };
    const valueLbl     = ctCreate.contract_value_pence != null
      ? ` · £${Math.round(ctCreate.contract_value_pence / 100).toLocaleString("en-GB")}`
      : "";
    const orderRefLbl  = ctCreate.order_ref ? ` · ${ctCreate.order_ref}` : "";
    const noteLbl      = ctCreate.counter_notes
      ? ` · "${ctCreate.counter_notes.length > 80 ? ctCreate.counter_notes.slice(0, 80) + "…" : ctCreate.counter_notes}"`
      : "";
    lcAudit({
      event_type: "contract_offer.created",
      ui_string:  `Contract offer → ${args.partnerName}${valueLbl}${orderRefLbl}${noteLbl}`,
      metadata:   {
        offer_id:           (data as ContractOffer).id,
        partner_company_id: args.partnerCompanyId,
        placement_id:       args.placementId || null,
        order_ref:          ctCreate.order_ref || null,
        contract_hash:      args.contractHash,
        contract_value_pence: ctCreate.contract_value_pence ?? null,
        counter_notes:      ctCreate.counter_notes || null,
      },
    });
    return data as ContractOffer;
  },
  async counterPropose(id: string, counterTerms: Record<string, unknown>, notes?: string) {
    if (!SUPABASE_READY || !id) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id;
    if (!userId) return null;
    const { data, error } = await supabase
      .from("contract_offers")
      .update({
        status:                      "countered",
        counter_terms:               counterTerms,
        counter_proposed_at:         new Date().toISOString(),
        counter_proposed_by_user_id: userId,
        response_meta:               notes ? { counter_notes: notes } : {},
      })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] contract_offers.counterPropose:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-contract-offers-changed", { detail: { id, status: "countered" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "contract_offer.countered", metadata: { offer_id: id, notes: notes || null } });
    return data as ContractOffer;
  },
  /* v50.278 — recounter creates a *new* contract_offer row with the
     revised terms instead of overwriting counter_terms on the prior
     row. The prior offer is marked status='countered' with response_
     meta linking to the new offer's id. This gives the Contract tab
     a true per-round audit lookback — every iteration is its own
     immutable row (own contract_hash, own offered_at, own responded_at)
     — and supports back-and-forth negotiation across N rounds without
     losing history. */
  async recounter(args: {
    fromOfferId:           string;
    contractTerms:         Record<string, unknown>;
    contractHash:          string;
    uiStringShown:         string;
    legalFrameworkVersion?: string;
    notes?:                string;
  }) {
    if (!SUPABASE_READY || !args.fromOfferId) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId    = sessionRes?.data?.session?.user?.id;
    const userEmail = sessionRes?.data?.session?.user?.email;
    if (!userId) return null;

    /* Load prior offer for the partner/coord/placement context. */
    const { data: prior, error: priorErr } = await supabase
      .from("contract_offers")
      .select("placement_id, partner_company_id, coordinator_company_id, offered_to_partner_name, offered_by_company")
      .eq("id", args.fromOfferId)
      .single();
    if (priorErr || !prior) { console.warn("[LABOURCORE_API] recounter prior load:", priorErr); return null; }

    /* Insert the new round first so we have an id to point response_
       meta on the old row at. */
    const newRow = {
      placement_id:            (prior as { placement_id?: string }).placement_id || null,
      coordinator_company_id:  (prior as { coordinator_company_id?: string }).coordinator_company_id,
      partner_company_id:      (prior as { partner_company_id?: string }).partner_company_id,
      offered_by:              userId,
      offered_by_email:        userEmail || "",
      offered_by_company:      (prior as { offered_by_company?: string }).offered_by_company || null,
      offered_to_partner_name: (prior as { offered_to_partner_name?: string }).offered_to_partner_name || "",
      contract_terms:          args.contractTerms,
      legal_framework_version: args.legalFrameworkVersion || "v1.1",
      contract_hash:           args.contractHash,
      ui_string_shown:         args.uiStringShown,
      status:                  "sent" as const,
      offered_at:              new Date().toISOString(),
      idempotency_key:         `counter_${args.fromOfferId}_${Date.now()}`,
      response_meta:           { prev_offer_id: args.fromOfferId, counter_notes: args.notes || null },
    };
    const { data: created, error: createErr } = await supabase
      .from("contract_offers")
      .insert(newRow)
      .select()
      .single();
    if (createErr || !created) { console.warn("[LABOURCORE_API] recounter insert:", createErr); return null; }
    const createdRow = created as ContractOffer;

    /* Mark prior offer as countered/superseded. Failure here is non-
       fatal — the new row is the source of truth going forward and
       the prior row's status can be reconciled later. */
    try {
      await supabase
        .from("contract_offers")
        .update({
          status:        "countered",
          responded_at:  new Date().toISOString(),
          response_meta: { superseded_by_offer_id: createdRow.id, counter_notes: args.notes || null },
        })
        .eq("id", args.fromOfferId);
    } catch (e) { console.warn("[LABOURCORE_API] recounter mark-prior:", e); }

    try { window.dispatchEvent(new CustomEvent("lc-contract-offers-changed", { detail: { id: createdRow.id, status: "sent", prev_offer_id: args.fromOfferId } })); } catch { /* ignore */ }

    /* v50.280 — write a human-readable ui_string into audit_events so
       the AuditLog page (and any future per-deal audit drawer) reads
       as a description, not just IDs. Compute a brief value delta
       inline and tack on the first ~80 chars of the sender's note. */
    const priorTerms     = ((prior as { contract_terms?: Record<string, unknown> }).contract_terms || {}) as { contract_value_pence?: number };
    const newTerms       = (args.contractTerms as { contract_value_pence?: number });
    const fmtP           = (p?: number) => p == null ? null : `£${Math.round(p / 100).toLocaleString("en-GB")}`;
    const priorValueStr  = fmtP(priorTerms.contract_value_pence);
    const newValueStr    = fmtP(newTerms.contract_value_pence);
    const valueLabel     = (priorValueStr && newValueStr && priorValueStr !== newValueStr)
      ? `${priorValueStr} → ${newValueStr}`
      : newValueStr || "";
    const notesTail      = args.notes
      ? ` · "${args.notes.length > 80 ? args.notes.slice(0, 80) + "…" : args.notes}"`
      : "";
    const uiString       = `Counter-proposal${valueLabel ? ` · ${valueLabel}` : ""}${notesTail}`;

    lcAudit({
      event_type: "contract_offer.recountered",
      ui_string:  uiString,
      metadata:   {
        new_offer_id:        createdRow.id,
        prev_offer_id:       args.fromOfferId,
        notes:               args.notes || null,
        contract_hash:       args.contractHash,
        prior_value_pence:   priorTerms.contract_value_pence ?? null,
        new_value_pence:     newTerms.contract_value_pence ?? null,
      },
    });
    return createdRow;
  },
  async respond(id: string, status: "accepted" | "rejected" | "withdrawn", meta?: Record<string, unknown>) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase
      .from("contract_offers")
      .update({
        status,
        responded_at:  new Date().toISOString(),
        response_meta: meta || {},
      })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] contract_offers.respond:", error); return null; }
    /* Side-effect: when accepted, bump the linked placement to
       'contracted' if there's one. Fire the global event so other
       pages refresh. */
    const updated = data as ContractOffer;
    if (status === "accepted" && updated.placement_id) {
      try {
        await supabase.from("placements")
          .update({ stage: "contracted", agreed_at: new Date().toISOString() })
          .eq("id", updated.placement_id)
          .neq("stage", "active")
          .neq("stage", "completed");
      } catch (e) { console.warn("[contract_offers.respond] placement bump:", e); }
    }
    try { window.dispatchEvent(new CustomEvent("lc-contract-offers-changed", { detail: { id, status } })); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent("lc-placements-changed", { detail: { source: "contract" } })); } catch { /* ignore */ }
    lcAudit({
      event_type: `contract_offer.${status}`,
      metadata:   { offer_id: id, placement_id: updated.placement_id || null },
    });
    return updated;
  },
});

/* v49.33 — compliance_requests (coord asks partner for a cert). */
const compliance_requests = Object.assign(table<ComplianceRequest>("compliance_requests"), {
  async listForPartner(partnerCompanyId: string) {
    if (!SUPABASE_READY || !partnerCompanyId) return null;
    const { data, error } = await supabase
      .from("compliance_requests")
      .select("*")
      .eq("partner_company_id", partnerCompanyId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) { console.warn("[LABOURCORE_API] compliance_requests.listForPartner:", error); return null; }
    return (data as ComplianceRequest[]) || [];
  },
  async listMineWithPartner(partnerCompanyId: string) {
    if (!SUPABASE_READY || !partnerCompanyId) return null;
    const co = getActiveCompanyId();
    if (!co) return null;
    const { data, error } = await supabase
      .from("compliance_requests")
      .select("*")
      .eq("coordinator_company_id", co)
      .eq("partner_company_id", partnerCompanyId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) { console.warn("[LABOURCORE_API] compliance_requests.listMineWithPartner:", error); return null; }
    return (data as ComplianceRequest[]) || [];
  },
  async create(args: { partnerCompanyId: string; certKind: string; certLabel?: string; notes?: string }) {
    if (!SUPABASE_READY) return null;
    const co = getActiveCompanyId();
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id;
    if (!co || !userId) return null;
    const { data, error } = await supabase.from("compliance_requests").insert({
      coordinator_company_id: co,
      partner_company_id:     args.partnerCompanyId,
      requested_by_user_id:   userId,
      cert_kind:              args.certKind,
      cert_label:             args.certLabel || null,
      notes:                  args.notes || null,
      status:                 "pending",
    }).select().single();
    if (error) { console.warn("[LABOURCORE_API] compliance_requests.create:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-compliance-requests-changed", { detail: { id: (data as ComplianceRequest).id, action: "create" } })); } catch { /* ignore */ }
    lcAudit({
      event_type: "compliance_request.created",
      ui_string:  args.certLabel || args.certKind,
      metadata:   { request_id: (data as ComplianceRequest).id, partner_company_id: args.partnerCompanyId, cert_kind: args.certKind },
    });
    return data as ComplianceRequest;
  },
  async markFulfilled(id: string, documentId?: string) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase
      .from("compliance_requests")
      .update({
        status:       "fulfilled",
        document_id:  documentId || null,
        fulfilled_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] compliance_requests.markFulfilled:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-compliance-requests-changed", { detail: { id, action: "fulfill" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "compliance_request.fulfilled", metadata: { request_id: id, document_id: documentId || null } });
    return data as ComplianceRequest;
  },
  async decline(id: string, reason?: string) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase
      .from("compliance_requests")
      .update({ status: "declined", declined_reason: reason || null })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] compliance_requests.decline:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-compliance-requests-changed", { detail: { id, action: "decline" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "compliance_request.declined", metadata: { request_id: id, reason: reason || null } });
    return data as ComplianceRequest;
  },
});

/* v49.40 — invoices (partner→coord). Bundles met / deemed_met milestones
   for the same placement, applies VAT, lifecycle draft → sent → paid /
   overdue / void. invoice_number is computed client-side via a deterministic
   per-partner per-month counter (queries existing rows to find max+1). */
const invoices = Object.assign(table<Invoice>("invoices"), {
  async listForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return null;
    const { data, error } = await supabase
      .from("invoices")
      .select("*")
      .eq("placement_id", placementId)
      .order("invoice_date", { ascending: false });
    if (error) { console.warn("[LABOURCORE_API] invoices.listForPlacement:", error); return null; }
    return (data as Invoice[]) || [];
  },
  async listForCompany(opts?: { side?: "coordinator" | "partner" }) {
    if (!SUPABASE_READY) return null;
    const co = getActiveCompanyId();
    if (!co) return null;
    const side = opts?.side || "partner";
    const col  = side === "coordinator" ? "coordinator_company_id" : "partner_company_id";
    const { data, error } = await supabase
      .from("invoices")
      .select("*, placement:placement_id(id, operative:operative_id(full_name), requirement:requirement_id(trade))")
      .eq(col, co)
      .order("invoice_date", { ascending: false })
      .limit(500);
    if (error) { console.warn("[LABOURCORE_API] invoices.listForCompany:", error); return null; }
    return data || [];
  },
  async create(args: {
    placementId:          string;
    coordinatorCompanyId: string;
    partnerCompanyId:     string;
    milestoneIds:         string[];
    subtotalPence:        number;
    vatRate:              number;        /* percent, e.g. 20 */
    paymentTermsDays?:    number;
    notes?:               string;
    invoiceDate?:         string;        /* YYYY-MM-DD; defaults to today */
  }) {
    if (!SUPABASE_READY) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    if (!userId) return null;
    /* Build the invoice number — per-partner per-month counter. */
    const today = args.invoiceDate ? new Date(args.invoiceDate) : new Date();
    const yyyymm = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}`;
    const partnerShort = args.partnerCompanyId.slice(0, 6).toUpperCase();
    const prefix = `INV-${partnerShort}-${yyyymm}`;
    /* Count existing matches to derive the next counter. */
    const { count } = await supabase
      .from("invoices")
      .select("id", { count: "exact", head: true })
      .eq("partner_company_id", args.partnerCompanyId)
      .like("invoice_number", `${prefix}-%`);
    const seq = (count || 0) + 1;
    const invoiceNumber = `${prefix}-${String(seq).padStart(3, "0")}`;

    const vatRate     = Number(args.vatRate) || 0;
    const subtotal    = Math.round(args.subtotalPence);
    const vat         = Math.round((subtotal * vatRate) / 100);
    const total       = subtotal + vat;
    const paymentDays = args.paymentTermsDays ?? 30;
    const dueDate = (() => {
      const d = new Date(today); d.setDate(d.getDate() + paymentDays);
      return d.toISOString().slice(0, 10);
    })();

    const row = {
      placement_id:           args.placementId,
      coordinator_company_id: args.coordinatorCompanyId,
      partner_company_id:     args.partnerCompanyId,
      invoice_number:         invoiceNumber,
      milestone_ids:          args.milestoneIds,
      subtotal_pence:         subtotal,
      vat_rate:               vatRate,
      vat_pence:              vat,
      total_pence:            total,
      status:                 "draft" as InvoiceStatus,
      invoice_date:           today.toISOString().slice(0, 10),
      due_date:               dueDate,
      payment_terms_days:     paymentDays,
      notes:                  args.notes?.trim() || null,
      created_by_user_id:     userId,
    };
    const { data, error } = await supabase.from("invoices").insert(row).select().single();
    if (error) { console.warn("[LABOURCORE_API] invoices.create:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-invoices-changed", { detail: { id: (data as Invoice).id, action: "create" } })); } catch { /* ignore */ }
    lcAudit({
      event_type: "invoice.created",
      ui_string:  invoiceNumber,
      metadata:   {
        invoice_id:    (data as Invoice).id,
        placement_id:  args.placementId,
        total_pence:   total,
        milestone_ids: args.milestoneIds,
      },
    });
    return data as Invoice;
  },
  async markSent(id: string) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase
      .from("invoices")
      .update({ status: "sent" as InvoiceStatus, sent_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] invoices.markSent:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-invoices-changed", { detail: { id, action: "sent" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "invoice.sent", ui_string: (data as Invoice).invoice_number, metadata: { invoice_id: id } });
    return data as Invoice;
  },
  async markPaid(id: string, notes?: string) {
    if (!SUPABASE_READY || !id) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const { data, error } = await supabase
      .from("invoices")
      .update({
        status:          "paid" as InvoiceStatus,
        paid_at:         new Date().toISOString(),
        paid_notes:      notes?.trim() || null,
        paid_by_user_id: userId,
      })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] invoices.markPaid:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-invoices-changed", { detail: { id, action: "paid" } })); } catch { /* ignore */ }
    lcAudit({
      event_type: "invoice.paid",
      ui_string:  (data as Invoice).invoice_number,
      metadata:   { invoice_id: id, total_pence: (data as Invoice).total_pence, notes: notes || null },
    });
    return data as Invoice;
  },
  async voidInvoice(id: string) {
    if (!SUPABASE_READY || !id) return null;
    const { data, error } = await supabase
      .from("invoices")
      .update({ status: "void" as InvoiceStatus })
      .eq("id", id)
      .select()
      .single();
    if (error) { console.warn("[LABOURCORE_API] invoices.voidInvoice:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-invoices-changed", { detail: { id, action: "void" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "invoice.voided", ui_string: (data as Invoice).invoice_number, metadata: { invoice_id: id } });
    return data as Invoice;
  },
});

/* v49.38 — placement_milestones (per-deal payment gates). Fan-out from
   contract_offers acceptance happens server-side (trigger); this namespace
   covers list + the three lifecycle actions partners + coords take. */
const placement_milestones = Object.assign(table<PlacementMilestone>("placement_milestones"), {
  async listForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return null;
    const { data, error } = await supabase
      .from("placement_milestones")
      .select("*")
      .eq("placement_id", placementId)
      .order("position", { ascending: true });
    if (error) { console.warn("[LABOURCORE_API] placement_milestones.listForPlacement:", error); return null; }
    return (data as PlacementMilestone[]) || [];
  },
  async submitEvidence(id: string, args: { url?: string; notes?: string }) {
    if (!SUPABASE_READY || !id) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const patch: Partial<PlacementMilestone> = {
      status:                "evidence_submitted",
      evidence_url:          args.url?.trim() || null,
      evidence_notes:        args.notes?.trim() || null,
      evidence_submitted_at: new Date().toISOString(),
      evidence_submitted_by: userId || undefined,
    };
    const { data, error } = await supabase.from("placement_milestones").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] placement_milestones.submitEvidence:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-milestones-changed", { detail: { id, action: "evidence" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "milestone.evidence_submitted", metadata: { milestone_id: id, evidence_url: args.url || null } });
    return data as PlacementMilestone;
  },
  async markMet(id: string, notes?: string) {
    if (!SUPABASE_READY || !id) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const patch: Partial<PlacementMilestone> = {
      status:                "met",
      marked_met_at:         new Date().toISOString(),
      marked_met_by_user_id: userId || undefined,
      marked_met_notes:      notes?.trim() || null,
    };
    const { data, error } = await supabase.from("placement_milestones").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] placement_milestones.markMet:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-milestones-changed", { detail: { id, action: "met" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "milestone.marked_met", metadata: { milestone_id: id, notes: notes || null } });
    return data as PlacementMilestone;
  },
  /* v49.41 — partner/coord-scoped milestone fetch. Drives the earnings
     forecast (Pipeline = met but uninvoiced; Future = pending future). */
  async listForCompany(opts?: { side?: "partner" | "coordinator" }) {
    if (!SUPABASE_READY) return null;
    const co = getActiveCompanyId();
    if (!co) return null;
    const side = opts?.side || "partner";
    const col = side === "coordinator" ? "coordinator_company_id" : "partner_company_id";
    /* First fetch the placement IDs scoped to the active company; then
       fetch milestones for those IDs. Two roundtrips beats relying on
       the implicit FK join semantics through PostgREST. */
    const { data: pls, error: plsErr } = await supabase
      .from("placements")
      .select("id")
      .eq(col, co)
      .is("deleted_at", null);
    if (plsErr) { console.warn("[LABOURCORE_API] placement_milestones.listForCompany placements:", plsErr); return null; }
    const ids = (pls || []).map(p => p.id);
    if (ids.length === 0) return [];
    const { data, error } = await supabase
      .from("placement_milestones")
      .select("*, placement:placement_id(id, coordinator_company_id, partner_company_id, requirement:requirement_id(trade), operative:operative_id(full_name))")
      .in("placement_id", ids)
      .order("agreed_date", { ascending: true });
    if (error) { console.warn("[LABOURCORE_API] placement_milestones.listForCompany:", error); return null; }
    return data || [];
  },
  async markDisputed(id: string, disputeId?: string) {
    if (!SUPABASE_READY || !id) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const patch: Partial<PlacementMilestone> = {
      status:              "disputed",
      disputed_at:         new Date().toISOString(),
      disputed_by_user_id: userId || undefined,
      dispute_id:          disputeId || null,
    };
    const { data, error } = await supabase.from("placement_milestones").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] placement_milestones.markDisputed:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-milestones-changed", { detail: { id, action: "disputed" } })); } catch { /* ignore */ }
    lcAudit({ event_type: "milestone.disputed", metadata: { milestone_id: id, dispute_id: disputeId || null } });
    return data as PlacementMilestone;
  },
});

/* v49.36 — disputes (trust-and-safety lifecycle). Either party of a
   placement can file; both parties can update status (server triggers
   notify the other side on INSERT + on status change). */
const disputes = Object.assign(table<Dispute>("disputes"), {
  async listForUser() {
    if (!SUPABASE_READY) return null;
    const co = getActiveCompanyId();
    if (!co) return null;
    const { data, error } = await supabase
      .from("disputes")
      .select("*, coord_co:coordinator_company_id(name), partner_co:partner_company_id(name), filer:filed_by_user_id(name,email)")
      .or(`coordinator_company_id.eq.${co},partner_company_id.eq.${co}`)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) { console.warn("[LABOURCORE_API] disputes.listForUser:", error); return null; }
    return (data as Dispute[]) || [];
  },
  async listForPlacement(placementId: string) {
    if (!SUPABASE_READY || !placementId) return null;
    const { data, error } = await supabase
      .from("disputes")
      .select("*, filer:filed_by_user_id(name,email)")
      .eq("placement_id", placementId)
      .order("created_at", { ascending: false });
    if (error) { console.warn("[LABOURCORE_API] disputes.listForPlacement:", error); return null; }
    return (data as Dispute[]) || [];
  },
  async create(args: {
    placementId?:           string | null;
    coordinatorCompanyId:   string;
    partnerCompanyId:       string;
    filedBySide:            DisputeSide;
    kind:                   Dispute["kind"];
    title:                  string;
    body:                   string;
    evidenceUrls?:          string[];
    /* v50.149 — line-level scope. Pass one (mutually exclusive). When
       set the dispute is rendered inline on the log/milestone row
       instead of only on the placement-level disputes list. */
    disputedLogId?:         string | null;
    disputedMilestoneId?:   string | null;
  }) {
    if (!SUPABASE_READY) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    if (!userId) return null;
    const row = {
      placement_id:           args.placementId || null,
      coordinator_company_id: args.coordinatorCompanyId,
      partner_company_id:     args.partnerCompanyId,
      filed_by_user_id:       userId,
      filed_by_side:          args.filedBySide,
      kind:                   args.kind,
      title:                  args.title.trim(),
      body:                   args.body.trim(),
      evidence_urls:          args.evidenceUrls || [],
      status:                 "pending" as DisputeStatus,
      disputed_log_id:        args.disputedLogId       || null,
      disputed_milestone_id:  args.disputedMilestoneId || null,
    };
    const { data, error } = await supabase.from("disputes").insert(row).select().single();
    if (error) { console.warn("[LABOURCORE_API] disputes.create:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-disputes-changed", { detail: { id: (data as Dispute).id, action: "create" } })); } catch { /* ignore */ }
    lcAudit({
      event_type: "dispute.filed",
      ui_string:  args.title,
      metadata:   {
        dispute_id:            (data as Dispute).id,
        placement_id:          args.placementId || null,
        kind:                  args.kind,
        side:                  args.filedBySide,
        disputed_log_id:       args.disputedLogId       || null,
        disputed_milestone_id: args.disputedMilestoneId || null,
      },
    });
    return data as Dispute;
  },
  async updateStatus(id: string, status: DisputeStatus, notes?: string) {
    if (!SUPABASE_READY || !id) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const patch: Partial<Dispute> = { status };
    if (notes && notes.trim()) patch.resolution_notes = notes.trim();
    if (status === "resolved" || status === "dismissed") {
      patch.resolved_at = new Date().toISOString();
      if (userId) patch.resolved_by_user_id = userId;
    }
    const { data, error } = await supabase.from("disputes").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] disputes.updateStatus:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-disputes-changed", { detail: { id, action: "status", status } })); } catch { /* ignore */ }
    lcAudit({ event_type: `dispute.${status}`, metadata: { dispute_id: id, notes: notes || null } });
    return data as Dispute;
  },
});

/* v50.150 — dispute_comments threading. Both sides can read + post on a
   dispute they're a member of (RLS gated in migration-104). The hook
   in DisputeThread subscribes to realtime updates so new comments
   surface within ~1s on the other side. */
const dispute_comments = Object.assign(table<DisputeComment>("dispute_comments"), {
  async listForDispute(disputeId: string) {
    if (!SUPABASE_READY || !disputeId) return null;
    const { data, error } = await supabase
      .from("dispute_comments")
      .select("*, author:author_user_id(name,email)")
      .eq("dispute_id", disputeId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });
    if (error) { console.warn("[LABOURCORE_API] dispute_comments.listForDispute:", error); return null; }
    return (data as DisputeComment[]) || [];
  },
  async post(args: {
    disputeId:  string;
    body:       string;
    authorSide: DisputeSide;
  }) {
    if (!SUPABASE_READY || !args.disputeId || !args.body.trim()) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    if (!userId) return null;
    const { data, error } = await supabase
      .from("dispute_comments")
      .insert({
        dispute_id:     args.disputeId,
        author_user_id: userId,
        author_side:    args.authorSide,
        body:           args.body.trim(),
      })
      .select("*, author:author_user_id(name,email)")
      .single();
    if (error) { console.warn("[LABOURCORE_API] dispute_comments.post:", error); return null; }
    try { window.dispatchEvent(new CustomEvent("lc-dispute-comments-changed", { detail: { id: (data as DisputeComment).id, dispute_id: args.disputeId } })); } catch { /* ignore */ }
    lcAudit({
      event_type: "dispute.commented",
      metadata:   { dispute_id: args.disputeId, comment_id: (data as DisputeComment).id, side: args.authorSide },
    });
    return data as DisputeComment;
  },
});

/* v49.23 — partner_onboarding (framework agreement per coord+partner). */
const partner_onboarding = Object.assign(table<PartnerOnboarding>("partner_onboarding"), {
  async findForPartner(partnerCompanyId: string) {
    if (!SUPABASE_READY || !partnerCompanyId) return null;
    const co = getActiveCompanyId();
    if (!co) return null;
    const { data, error } = await supabase
      .from("partner_onboarding")
      .select("*")
      .eq("coordinator_company_id", co)
      .eq("partner_company_id", partnerCompanyId)
      .maybeSingle();
    if (error) { console.warn("[LABOURCORE_API] partner_onboarding.findForPartner:", error); return null; }
    return data as PartnerOnboarding | null;
  },
  async propose(args: {
    partnerCompanyId: string;
    slaTier?:         string;
    feeTier?:         number;
    notes?:           string;
    sign?:            boolean; /* coord can sign on behalf of both = status='accepted' */
  }) {
    if (!SUPABASE_READY) return null;
    const co = getActiveCompanyId();
    if (!co) return null;
    const sessionRes = await supabase.auth.getSession();
    const userId = sessionRes?.data?.session?.user?.id || null;
    const status: PartnerOnboarding["status"] = args.sign ? "accepted" : "proposed";
    const { data, error } = await supabase.from("partner_onboarding").upsert({
      coordinator_company_id: co,
      partner_company_id:     args.partnerCompanyId,
      sla_tier:               args.slaTier,
      fee_tier:               args.feeTier,
      status,
      proposed_by_user_id:    userId,
      notes:                  args.notes,
      signed_at:              args.sign ? new Date().toISOString() : null,
    }, { onConflict: "coordinator_company_id,partner_company_id" }).select().single();
    if (error) { console.warn("[LABOURCORE_API] partner_onboarding.propose:", error); return null; }
    lcAudit({
      event_type: `partner_onboarding.${status}`,
      metadata:   { onboarding_id: (data as PartnerOnboarding).id, partner_company_id: args.partnerCompanyId, sla_tier: args.slaTier, fee_tier: args.feeTier },
    });
    return data as PartnerOnboarding;
  },
  async setStatus(id: string, status: PartnerOnboarding["status"]) {
    if (!SUPABASE_READY || !id) return null;
    const patch: Partial<PartnerOnboarding> = { status };
    if (status === "accepted" || status === "active") patch.signed_at = new Date().toISOString();
    const { data, error } = await supabase.from("partner_onboarding").update(patch).eq("id", id).select().single();
    if (error) { console.warn("[LABOURCORE_API] partner_onboarding.setStatus:", error); return null; }
    lcAudit({ event_type: `partner_onboarding.${status}`, metadata: { onboarding_id: id } });
    return data as PartnerOnboarding;
  },
});

/* v48.5 — audit_events. Local-only fallback handled by the audit helper
   (src/lib/audit.ts) — this just exposes the Supabase append. */
const audit = {
  async append(row: Record<string, unknown>) {
    if (!SUPABASE_READY) return null;
    const { data, error } = await supabase.from("audit_events").insert(row).select().single();
    if (error) { console.warn("[LABOURCORE_API] audit_events insert:", error); return null; }
    return data;
  },
  list: (filter?: Record<string, unknown>) => table("audit_events").list(filter),
};

/* The full exported namespace. Mirrors the v48.311 IIFE return value. */
export const LABOURCORE_API = {
  setActiveCompanyId,
  getActiveCompanyId,
  isReady: () => SUPABASE_READY,
  auth,
  /* Brief tables (Phase 1+). */
  companies,
  memberships,
  /* v49.64 — Per-user avatar + cover image upload. Bucket is
     'profile-images', public-read, owner-write enforced by RLS
     (first path segment must equal auth.uid()). */
  profile_images: {
    async upload(kind: "avatar" | "cover", file: File): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const sessionRes = await supabase.auth.getSession();
      const userId = sessionRes?.data?.session?.user?.id;
      if (!userId) { console.warn("[profile_images.upload] no session"); return null; }
      /* Size + mime gate. 5MB cap, image/* only. */
      if (file.size > 5 * 1024 * 1024) {
        console.warn("[profile_images.upload] file > 5MB"); return null;
      }
      if (!file.type.startsWith("image/")) {
        console.warn("[profile_images.upload] not an image"); return null;
      }
      const ext = (file.name.match(/\.([a-z0-9]+)$/i)?.[1] || "jpg").toLowerCase();
      const path = `${userId}/${kind}-${Date.now()}.${ext}`;
      try {
        const { error: upErr } = await supabase.storage
          .from("profile-images")
          .upload(path, file, { upsert: false, cacheControl: "3600", contentType: file.type });
        if (upErr) { console.warn("[profile_images.upload] storage:", upErr); return null; }
        const { data: pub } = supabase.storage.from("profile-images").getPublicUrl(path);
        const publicUrl = pub?.publicUrl || null;
        if (!publicUrl) return null;
        /* Write the URL back to the profile + clean up the previous
           object to keep the bucket tidy. */
        const colName = kind === "avatar" ? "avatar_url" : "cover_url";
        const { data: prev } = await supabase.from("profiles").select(colName).eq("id", userId).maybeSingle();
        const prevUrl = (prev as Record<string, string | null> | null)?.[colName] || null;
        await supabase.from("profiles").update({ [colName]: publicUrl }).eq("id", userId);
        if (prevUrl && prevUrl.includes("/profile-images/")) {
          /* Best-effort cleanup of the prior object. */
          const prefix = `${userId}/`;
          const idx = prevUrl.lastIndexOf(prefix);
          if (idx >= 0) {
            const oldPath = prevUrl.slice(idx);
            try { await supabase.storage.from("profile-images").remove([oldPath]); } catch { /* ignore */ }
          }
        }
        lcAudit({ event_type: `profile_image.${kind}_uploaded`, metadata: { path, size: file.size } });
        try { window.dispatchEvent(new Event("lc-auth-changed")); } catch { /* ignore */ }
        return publicUrl;
      } catch (e) {
        console.warn("[profile_images.upload] threw:", e);
        return null;
      }
    },
    /* v50.196 — Company logo upload for the onboarding wizard. Reuses the
       same public 'profile-images' bucket + RLS (first segment must be
       auth.uid()), but does NOT write to any profile column — the
       returned URL is stashed in onboarding jsonb (OnbCompany.logoUrl /
       OnbBuyerProfile.logoUrl) so it lives with the rest of the
       submission. */
    async uploadCompanyLogo(file: File): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const sessionRes = await supabase.auth.getSession();
      const userId = sessionRes?.data?.session?.user?.id;
      if (!userId) { console.warn("[profile_images.uploadCompanyLogo] no session"); return null; }
      if (file.size > 5 * 1024 * 1024) { console.warn("[profile_images.uploadCompanyLogo] file > 5MB"); return null; }
      if (!file.type.startsWith("image/")) { console.warn("[profile_images.uploadCompanyLogo] not an image"); return null; }
      const ext = (file.name.match(/\.([a-z0-9]+)$/i)?.[1] || "png").toLowerCase();
      const path = `${userId}/company-logo-${Date.now()}.${ext}`;
      try {
        const { error: upErr } = await supabase.storage
          .from("profile-images")
          .upload(path, file, { upsert: false, cacheControl: "3600", contentType: file.type });
        if (upErr) { console.warn("[profile_images.uploadCompanyLogo] storage:", upErr); return null; }
        const { data: pub } = supabase.storage.from("profile-images").getPublicUrl(path);
        const publicUrl = pub?.publicUrl || null;
        if (!publicUrl) return null;
        lcAudit({ event_type: "profile_image.company_logo_uploaded", metadata: { path, size: file.size } });
        return publicUrl;
      } catch (e) {
        console.warn("[profile_images.uploadCompanyLogo] threw:", e);
        return null;
      }
    },
    async clear(kind: "avatar" | "cover"): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const sessionRes = await supabase.auth.getSession();
      const userId = sessionRes?.data?.session?.user?.id;
      if (!userId) return false;
      const colName = kind === "avatar" ? "avatar_url" : "cover_url";
      const { data: prev } = await supabase.from("profiles").select(colName).eq("id", userId).maybeSingle();
      const prevUrl = (prev as Record<string, string | null> | null)?.[colName] || null;
      await supabase.from("profiles").update({ [colName]: null }).eq("id", userId);
      if (prevUrl && prevUrl.includes("/profile-images/")) {
        const idx = prevUrl.lastIndexOf(`${userId}/`);
        if (idx >= 0) {
          const oldPath = prevUrl.slice(idx);
          try { await supabase.storage.from("profile-images").remove([oldPath]); } catch { /* ignore */ }
        }
      }
      lcAudit({ event_type: `profile_image.${kind}_cleared` });
      try { window.dispatchEvent(new Event("lc-auth-changed")); } catch { /* ignore */ }
      return true;
    },
  },

  /* v49.66 — onboarding wizard: drafts + submissions.
     Adapted from lc-onboarding-wizard-prompt-r2 to the post-signup
     placement. Drafts live in `onboarding_drafts` (one row per user,
     own-row RLS). On final submit the SECURITY DEFINER RPC
     `submit_onboarding` atomically moves the draft into
     `onboarding_submissions`, sets profile.onboarding_complete=true,
     and deletes the draft. */
  onboarding: {
    async loadDraft(): Promise<{ role: string; step: string; data: Record<string, unknown> } | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("onboarding_drafts")
        .select("role, step, data")
        .maybeSingle();
      if (error) { console.warn("[onboarding.loadDraft]", error); return null; }
      return data ? { role: data.role, step: data.step, data: (data.data || {}) as Record<string, unknown> } : null;
    },
    async saveDraft(args: { role: "partner" | "coordinator" | "hybrid"; step: string; data: Record<string, unknown> }): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const sessionRes = await supabase.auth.getSession();
      const userId = sessionRes?.data?.session?.user?.id;
      if (!userId) return false;
      const { error } = await supabase
        .from("onboarding_drafts")
        .upsert({ user_id: userId, role: args.role, step: args.step, data: args.data }, { onConflict: "user_id" });
      if (error) { console.warn("[onboarding.saveDraft]", error); return false; }
      return true;
    },
    async submit(args: {
      data:     Record<string, unknown>;
      consents: Record<string, unknown>;
      flags:    string[];
    }): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("submit_onboarding", {
        p_data:     args.data,
        p_consents: args.consents,
        p_flags:    args.flags,
      });
      if (error) { console.warn("[onboarding.submit]", error); return null; }
      lcAudit({ event_type: "onboarding.submitted", ui_string: String(data || ""), metadata: { flags: args.flags } });
      try { window.dispatchEvent(new Event("lc-auth-changed")); } catch { /* ignore */ }
      return (data as string) || null;
    },
    async listSubmissionsForCurrentUser(): Promise<Array<{ id: string; submitted_at: string; status: string; role: string }>> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("onboarding_submissions")
        .select("id, submitted_at, status, role")
        .order("submitted_at", { ascending: false });
      if (error) { console.warn("[onboarding.listSubmissions]", error); return []; }
      return (data || []) as never;
    },
    /* v49.74 — the latest submission row for the current user with
       every field the /me status surface + wizard-resume flow need.
       Own-row RLS keeps it scoped to the caller. Returns null when
       the user has never submitted. */
    /* v49.93 — capability profile lookup for the bid-engine pattern-match
       scorer. Reads from the user's latest approved onboarding submission
       (own-row read RLS) and surfaces just the capability dimensions the
       scorer cares about. Returns null when there's no approved submission. */
    async getMyCapabilityProfile(): Promise<{
      hyperscale?: "none" | "tier_ii" | "tier_iii" | "tier_iv";
      cleanroom?:  boolean;
      commissioning?: boolean;
      dc_experience?: "none" | "some" | "significant" | "specialist";
      trades_supplied?: string[];
    } | null> {
      if (!SUPABASE_READY) return null;
      /* v49.99 — read the mutable partner_capability_profiles row first.
         RLS limits the row to caller's company. Fall back to the
         onboarding snapshot when no row exists yet (fresh approvals
         before backfill / before self-edit). */
      const { data: pcp } = await supabase
        .from("partner_capability_profiles")
        .select("hyperscale_experience, cleanroom, commissioning, dc_experience, trades_supplied")
        .maybeSingle();
      if (pcp) {
        return {
          hyperscale:      (pcp.hyperscale_experience as "none" | "tier_ii" | "tier_iii" | "tier_iv" | null) ?? undefined,
          cleanroom:       !!pcp.cleanroom,
          commissioning:   !!pcp.commissioning,
          dc_experience:   (pcp.dc_experience as "none" | "some" | "significant" | "specialist" | null) ?? undefined,
          trades_supplied: (pcp.trades_supplied as string[]) || [],
        };
      }
      const { data, error } = await supabase
        .from("onboarding_submissions")
        .select("data")
        .eq("status", "approved")
        .order("reviewed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error || !data) return null;
      const caps = ((data.data as Record<string, unknown> | null)?.capabilities || {}) as Record<string, unknown>;
      return {
        hyperscale:      (caps.hyperscaleExperience as "none" | "tier_ii" | "tier_iii" | "tier_iv" | undefined) ?? undefined,
        cleanroom:       !!caps.cleanroom,
        commissioning:   !!caps.commissioning,
        dc_experience:   (caps.dcExperience as "none" | "some" | "significant" | "specialist" | undefined) ?? undefined,
        trades_supplied: Array.isArray(caps.tradesSupplied) ? (caps.tradesSupplied as string[]) : [],
      };
    },
    async upsertMyCapabilityProfile(args: {
      companyId:             string;
      hyperscaleExperience?: "none" | "tier_ii" | "tier_iii" | "tier_iv" | null;
      cleanroom:             boolean;
      commissioning:         boolean;
      dcExperience?:         "none" | "some" | "significant" | "specialist" | null;
      tradesSupplied:        string[];
    }): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("upsert_my_capability_profile", {
        p_company_id:            args.companyId,
        p_hyperscale_experience: args.hyperscaleExperience ?? null,
        p_cleanroom:             args.cleanroom,
        p_commissioning:         args.commissioning,
        p_dc_experience:         args.dcExperience ?? null,
        p_trades_supplied:       args.tradesSupplied,
      });
      if (error) { console.warn("[onboarding.upsertMyCapabilityProfile]", error); return false; }
      lcAudit({ event_type: "capability_profile.updated", metadata: { company_id: args.companyId } });
      return true;
    },
    async getLatestSubmission(): Promise<{
      id:           string;
      submitted_at: string;
      role:         string;
      /* v49.76 — 'superseded' added when a more_info_needed row is
         auto-marked stale by a follow-up submission. */
      status:       "pending" | "approved" | "rejected" | "more_info_needed" | "superseded";
      reviewed_at:  string | null;
      review_notes: string | null;
      flags:        string[];
      data:         Record<string, unknown>;
    } | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("onboarding_submissions")
        .select("id, submitted_at, role, status, reviewed_at, review_notes, flags, data")
        .order("submitted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) { console.warn("[onboarding.getLatestSubmission]", error); return null; }
      return (data as never) || null;
    },
    /* v49.69 — admin queue surface. Reads onboarding_submissions; row
       visibility is gated by the onb_subs_admin_read RLS policy
       (profiles.is_admin = true). The three actions are SECURITY DEFINER
       RPCs that perform the same admin check + write reviewed_by/at +
       update status. */
    async adminListSubmissions(args: { status?: string; role?: string; limit?: number } = {}): Promise<Array<{ id: string; user_id: string; company_id: string | null; submitted_at: string; status: string; role: string; flags: string[]; reviewed_by: string | null; reviewed_at: string | null; review_notes: string | null; data: Record<string, unknown>; consents: Record<string, unknown> }>> {
      if (!SUPABASE_READY) return [];
      let q = supabase
        .from("onboarding_submissions")
        .select("id, user_id, company_id, submitted_at, status, role, flags, reviewed_by, reviewed_at, review_notes, data, consents")
        .order("submitted_at", { ascending: true })
        .limit(args.limit ?? 200);
      if (args.status) q = q.eq("status", args.status);
      if (args.role)   q = q.eq("role", args.role);
      const { data, error } = await q;
      if (error) { console.warn("[onboarding.adminListSubmissions]", error); return []; }
      return (data || []) as never;
    },
    async adminApprove(submissionId: string, setTrusted: boolean, notes: string | null): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("admin_approve_onboarding", {
        p_submission_id: submissionId,
        p_set_trusted:   setTrusted,
        p_notes:         notes,
      });
      if (error) { console.warn("[onboarding.adminApprove]", error); return false; }
      lcAudit({ event_type: "onboarding.admin_approved", metadata: { submission_id: submissionId, set_trusted: setTrusted } });
      return true;
    },
    async adminReject(submissionId: string, reason: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("admin_reject_onboarding", {
        p_submission_id: submissionId,
        p_reason:        reason,
      });
      if (error) { console.warn("[onboarding.adminReject]", error); return false; }
      lcAudit({ event_type: "onboarding.admin_rejected", metadata: { submission_id: submissionId } });
      return true;
    },
    async adminRequestMoreInfo(submissionId: string, gaps: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("admin_request_more_info_onboarding", {
        p_submission_id: submissionId,
        p_gaps:          gaps,
      });
      if (error) { console.warn("[onboarding.adminRequestMoreInfo]", error); return false; }
      lcAudit({ event_type: "onboarding.admin_more_info_requested", metadata: { submission_id: submissionId } });
      return true;
    },
    /* v49.80 — softer reopen of a rejected row. Flips back to
       more_info_needed with new gaps text so the user gets the
       existing /me Resume CTA. */
    async adminReopenRejected(submissionId: string, gaps: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("admin_reopen_rejected_onboarding", {
        p_submission_id: submissionId,
        p_gaps:          gaps,
      });
      if (error) { console.warn("[onboarding.adminReopenRejected]", error); return false; }
      lcAudit({ event_type: "onboarding.admin_reopened", metadata: { submission_id: submissionId } });
      return true;
    },
  },

  /* v49.80 — admin email queue retry. Resets attempts/next_retry_at/
     failed_at on a dead row so the next cron pass picks it up. */
  email_admin: {
    async retryOutbox(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { data, error } = await supabase.rpc("admin_retry_email_outbox", { p_id: id });
      if (error) { console.warn("[email_admin.retryOutbox]", error); return false; }
      if (data) lcAudit({ event_type: "email.admin_retry_outbox", metadata: { id } });
      return data === true;
    },
    async retryPlacement(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { data, error } = await supabase.rpc("admin_retry_placement_email", { p_id: id });
      if (error) { console.warn("[email_admin.retryPlacement]", error); return false; }
      if (data) lcAudit({ event_type: "email.admin_retry_placement", metadata: { id } });
      return data === true;
    },
    async listFailedOutbox(limit = 50): Promise<Array<{ id: string; to_email: string; kind: string; error: string | null; attempts: number; failed_at: string }>> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("email_outbox")
        .select("id, to_email, kind, error, attempts, failed_at")
        .not("failed_at", "is", null)
        .order("failed_at", { ascending: false })
        .limit(limit);
      if (error) { console.warn("[email_admin.listFailedOutbox]", error); return []; }
      return (data as never) || [];
    },
    async listFailedPlacement(limit = 50): Promise<Array<{ id: string; kind: string; email_error: string | null; attempts: number; email_failed_at: string }>> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("placement_notifications")
        .select("id, kind, email_error, attempts, email_failed_at")
        .not("email_failed_at", "is", null)
        .order("email_failed_at", { ascending: false })
        .limit(limit);
      if (error) { console.warn("[email_admin.listFailedPlacement]", error); return []; }
      return (data as never) || [];
    },
  },

  /* v49.85 — Move 3 Bid Engine scaffold. Lists open opportunities for
     authenticated browsers; lets partners register interest via the
     SECURITY DEFINER RPC. Pack assembly + submit + outcome marking
     come in subsequent versions. */
  bid_opportunities: {
    async listOpen(args: { primaryTrade?: string; limit?: number } = {}): Promise<BidOpportunity[]> {
      if (!SUPABASE_READY) return [];
      let q = supabase
        .from("bid_opportunities")
        .select("*")
        .order("tender_closes_at", { ascending: true, nullsFirst: false })
        .limit(args.limit ?? 100);
      if (args.primaryTrade) q = q.eq("primary_trade", args.primaryTrade);
      const { data, error } = await q;
      if (error) { console.warn("[bid_opportunities.listOpen]", error); return []; }
      return (data as BidOpportunity[]) || [];
    },
    async get(id: string): Promise<BidOpportunity | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("bid_opportunities")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) { console.warn("[bid_opportunities.get]", error); return null; }
      return (data as BidOpportunity) || null;
    },
    async registerInterest(opportunityId: string): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("register_bid_interest", {
        p_opportunity_id: opportunityId,
      });
      if (error) { console.warn("[bid_opportunities.registerInterest]", error); return null; }
      lcAudit({ event_type: "bid_interest_registered", metadata: { opportunity_id: opportunityId, pack_id: data } });
      return (data as string) || null;
    },
    /* v49.91 — direct INSERT for the /tenders/new form. RLS's
       originator_all policy gates write to members of the
       originated_by_company_id, which we set to the caller's primary
       company. Returns the new id on success. */
    async create(args: {
      originatedBy: "coordinator" | "partner";
      companyId:    string;
      userId:       string;
      title:        string;
      description?: string | null;
      clientName?:  string | null;
      operatorName?: string | null;
      location?:    string | null;
      primaryTrade?: string | null;
      trades?:      string[];
      valueBand?:   string | null;
      programmeStart?: string | null;
      programmeEnd?: string | null;
      tenderPublishedAt?: string | null;
      tenderClosesAt?: string | null;
      packRequirements?: string | null;
      /* v49.93 — capability requirements (NULL = no constraint). */
      requiresHyperscaleTier?: "tier_ii" | "tier_iii" | "tier_iv" | null;
      requiresCleanroom?:      boolean;
      requiresCommissioning?:  boolean;
      dcRelevance?:            "colocation" | "hyperscale" | "mission_critical" | null;
      /* v50.37 — required document labels (free text, one per array
         element). Empty / undefined means no specific requirements. */
      requiredDocumentLabels?: string[];
    }): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("bid_opportunities")
        .insert({
          originated_by:            args.originatedBy,
          originated_by_user_id:    args.userId,
          originated_by_company_id: args.companyId,
          title:                    args.title,
          description:              args.description       || null,
          client_name:              args.clientName        || null,
          operator_name:            args.operatorName      || null,
          location:                 args.location          || null,
          primary_trade:            args.primaryTrade      || null,
          trades:                   args.trades            || [],
          value_band:               args.valueBand         || null,
          programme_start:          args.programmeStart    || null,
          programme_end:            args.programmeEnd      || null,
          tender_published_at:      args.tenderPublishedAt || null,
          tender_closes_at:         args.tenderClosesAt    || null,
          pack_requirements:        args.packRequirements  || null,
          requires_hyperscale_tier: args.requiresHyperscaleTier || null,
          requires_cleanroom:       !!args.requiresCleanroom,
          requires_commissioning:   !!args.requiresCommissioning,
          dc_relevance:             args.dcRelevance       || null,
          required_document_labels: args.requiredDocumentLabels || [],
          status:                   "open",
        })
        .select("id")
        .single();
      if (error) { console.warn("[bid_opportunities.create]", error); return null; }
      const id = (data as { id: string }).id;
      lcAudit({ event_type: "bid_opportunity_surfaced", metadata: { id, originated_by: args.originatedBy } });
      return id;
    },
    /* v49.94 — UPDATE for originator-edit. RLS's originator_all policy
       gates writes to members of originated_by_company_id. Only allow
       edits while the opportunity is still open — closed/awarded/
       cancelled rows are historical. */
    async update(id: string, patch: Partial<BidOpportunity>): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase
        .from("bid_opportunities")
        .update(patch)
        .eq("id", id)
        .eq("status", "open");
      if (error) { console.warn("[bid_opportunities.update]", error); return false; }
      lcAudit({ event_type: "bid_opportunity_edited", metadata: { id, keys: Object.keys(patch) } });
      return true;
    },
    /* v49.94 — Coord-initiated close. Flips status open → closed. RLS
       gates by originator membership. Cancelled is reserved for admin. */
    async close(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase
        .from("bid_opportunities")
        .update({ status: "closed" })
        .eq("id", id)
        .eq("status", "open");
      if (error) { console.warn("[bid_opportunities.close]", error); return false; }
      lcAudit({ event_type: "bid_opportunity_closed", metadata: { id } });
      return true;
    },
    /* v49.98 — publish a material-amendment notice. Records a
       tender_amendments row and fans out a bundled email to every
       partner company with a bid_pack against the opp. Originator
       only (server-enforced). */
    async publishAmendment(id: string, summary: string): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("publish_tender_amendment", {
        p_opportunity_id: id,
        p_summary:        summary,
      });
      if (error) { console.warn("[bid_opportunities.publishAmendment]", error); return null; }
      lcAudit({ event_type: "tender_amendment.published", metadata: { id: data, opportunity_id: id } });
      return (data as string) || null;
    },
    /* v50.27 — coordinator-side push: invite specific partner
       companies to bid. Idempotent via DB unique constraint —
       re-invites are silently dropped, only new rows count. */
    async invitePartners(opportunityId: string, partnerCompanyIds: string[]): Promise<number | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("invite_partners_to_tender", {
        p_opportunity_id:      opportunityId,
        p_partner_company_ids: partnerCompanyIds,
      });
      if (error) { console.warn("[bid_opportunities.invitePartners]", error); return null; }
      lcAudit({ event_type: "tender_invitations.sent", metadata: { opportunity_id: opportunityId, count: data as number, partner_company_ids: partnerCompanyIds } });
      return (data as number) ?? 0;
    },
    async listInvitations(opportunityId: string): Promise<TenderInvitation[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("tender_invitations")
        .select("*")
        .eq("opportunity_id", opportunityId)
        /* v50.42 — filter cancelled invites from the list view. */
        .is("deleted_at", null)
        .order("invited_at", { ascending: false });
      if (error) { console.warn("[bid_opportunities.listInvitations]", error); return []; }
      return (data as TenderInvitation[]) || [];
    },
    /* v50.42 — soft-delete an unaccepted invitation. Originator-only,
       enforced by the RPC. Idempotent. */
    async cancelInvitation(invitationId: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("cancel_tender_invitation", {
        p_invitation_id: invitationId,
      });
      if (error) { console.warn("[bid_opportunities.cancelInvitation]", error); return false; }
      lcAudit({ event_type: "tender_invitation.cancelled", metadata: { id: invitationId } });
      return true;
    },
    /* v50.43 — manually trigger a reminder before the v50.30 cron would.
       One nudge max per invitation enforced by the RPC. */
    async remindInvitation(invitationId: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("send_tender_invitation_reminder", {
        p_invitation_id: invitationId,
      });
      if (error) { console.warn("[bid_opportunities.remindInvitation]", error); return false; }
      lcAudit({ event_type: "tender_invitation.reminder_sent", metadata: { id: invitationId } });
      return true;
    },
    /* v50.33 — tender documents (Slice A). Upload to private Storage
       bucket + insert metadata row. Returns the row on success, null
       on any failure. Path convention: <opp_id>/<ts>-<safe-name> so
       Storage RLS can extract the opp id with split_part(name, '/', 1). */
    async uploadTenderDocument(opportunityId: string, file: File, label?: string): Promise<TenderDocument | null> {
      if (!SUPABASE_READY) return null;
      const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
      const path     = `${opportunityId}/${Date.now()}-${safeName}`;
      try {
        const { error: upErr } = await supabase.storage
          .from("tender-docs")
          .upload(path, file, { upsert: false, cacheControl: "3600", contentType: file.type });
        if (upErr) { console.warn("[bid_opportunities.uploadTenderDocument] storage:", upErr); return null; }
        const sessionRes = await supabase.auth.getSession();
        const userId = sessionRes?.data?.session?.user?.id || null;
        const { data: row, error: insErr } = await supabase
          .from("tender_documents")
          .insert({
            opportunity_id:      opportunityId,
            file_path:           path,
            file_name:           file.name,
            file_size:           file.size,
            mime_type:           file.type || null,
            label:               label || null,
            uploaded_by_user_id: userId,
          })
          .select().single();
        if (insErr) {
          /* Best-effort: cleanup the orphan blob since the metadata
             insert failed. The blob can't be discovered any other way
             once the row is missing. */
          try { await supabase.storage.from("tender-docs").remove([path]); } catch { /* ignore */ }
          console.warn("[bid_opportunities.uploadTenderDocument] insert:", insErr);
          return null;
        }
        lcAudit({
          event_type: "tender_document.uploaded",
          ui_string:  label || file.name,
          metadata:   { document_id: (row as TenderDocument).id, opportunity_id: opportunityId, file_size: file.size, mime: file.type || null },
        });
        return row as TenderDocument;
      } catch (e) {
        console.warn("[bid_opportunities.uploadTenderDocument] threw:", e);
        return null;
      }
    },
    async listTenderDocuments(opportunityId: string): Promise<TenderDocument[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("tender_documents")
        .select("*")
        .eq("opportunity_id", opportunityId)
        .is("deleted_at", null)
        .order("uploaded_at", { ascending: false });
      if (error) { console.warn("[bid_opportunities.listTenderDocuments]", error); return []; }
      return (data as TenderDocument[]) || [];
    },
    async tenderDocumentSignedUrl(filePath: string, ttlSeconds = 3600): Promise<string | null> {
      if (!SUPABASE_READY || !filePath) return null;
      const { data, error } = await supabase.storage.from("tender-docs").createSignedUrl(filePath, ttlSeconds);
      if (error) { console.warn("[bid_opportunities.tenderDocumentSignedUrl]", error); return null; }
      return data?.signedUrl || null;
    },
    /* Soft-delete: mark deleted_at on the row but leave the blob in
       Storage so the audit trail survives. Originator-only via RLS. */
    async deleteTenderDocument(documentId: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase
        .from("tender_documents")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", documentId);
      if (error) { console.warn("[bid_opportunities.deleteTenderDocument]", error); return false; }
      lcAudit({ event_type: "tender_document.deleted", metadata: { document_id: documentId } });
      return true;
    },
    async listAmendments(id: string): Promise<TenderAmendment[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("tender_amendments")
        .select("*")
        .eq("opportunity_id", id)
        .order("created_at", { ascending: false });
      if (error) { console.warn("[bid_opportunities.listAmendments]", error); return []; }
      return (data as TenderAmendment[]) || [];
    },
  },

  /* v50.13 — per-user saved tender searches. RLS gates all read +
     write to user_id = auth.uid(), so namespace is thin. */
  saved_tender_searches: {
    async list(): Promise<SavedTenderSearch[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("saved_tender_searches")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) { console.warn("[saved_tender_searches.list]", error); return []; }
      return (data as SavedTenderSearch[]) || [];
    },
    async create(name: string, filters: SavedTenderSearch["filters"]): Promise<SavedTenderSearch | null> {
      if (!SUPABASE_READY) return null;
      const sess = await supabase.auth.getSession();
      const uid  = sess?.data?.session?.user?.id;
      if (!uid) return null;
      const { data, error } = await supabase
        .from("saved_tender_searches")
        .insert({ user_id: uid, name, filters })
        .select("*").single();
      if (error) { console.warn("[saved_tender_searches.create]", error); return null; }
      lcAudit({ event_type: "saved_tender_search.created", metadata: { id: data.id, name } });
      return data as SavedTenderSearch;
    },
    async delete(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.from("saved_tender_searches").delete().eq("id", id);
      if (error) { console.warn("[saved_tender_searches.delete]", error); return false; }
      lcAudit({ event_type: "saved_tender_search.deleted", metadata: { id } });
      return true;
    },
  },

  bid_packs: {
    async listForCompany(companyId: string): Promise<BidPack[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("bid_packs")
        .select("*")
        .eq("partner_company_id", companyId)
        .order("created_at", { ascending: false });
      if (error) { console.warn("[bid_packs.listForCompany]", error); return []; }
      return (data as BidPack[]) || [];
    },
    async getForOpportunity(opportunityId: string, partnerCompanyId: string): Promise<BidPack | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("bid_packs")
        .select("*")
        .eq("bid_opportunity_id", opportunityId)
        .eq("partner_company_id", partnerCompanyId)
        .maybeSingle();
      if (error) { console.warn("[bid_packs.getForOpportunity]", error); return null; }
      return (data as BidPack) || null;
    },
    /* v49.86 — autosave the partner's draft pack. RLS gates the UPDATE
       to members of the partner_company_id. Status auto-flips from
       interest_registered → draft on first edit. Returns true on
       success. */
    async updateDraft(packId: string, patch: Partial<BidPack>): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const next: Record<string, unknown> = { ...patch };
      /* If the caller hasn't explicitly set status, bump it to 'draft'
         so the pack progresses out of interest_registered on first edit. */
      if (!("status" in next)) next.status = "draft";
      const { error } = await supabase
        .from("bid_packs")
        .update(next)
        .eq("id", packId)
        .in("status", ["interest_registered", "draft"]); /* don't clobber submitted+ packs */
      if (error) { console.warn("[bid_packs.updateDraft]", error); return false; }
      return true;
    },
    /* v49.87 — Silver+ trust gate check + submit + withdraw. */
    async eligibility(companyId: string): Promise<{ eligible: boolean; reason?: string; progression?: string; completed_placements?: number }> {
      if (!SUPABASE_READY) return { eligible: false, reason: "supabase_not_ready" };
      const { data, error } = await supabase.rpc("partner_bid_eligibility", { p_company_id: companyId });
      if (error) { console.warn("[bid_packs.eligibility]", error); return { eligible: false, reason: "rpc_error" }; }
      return (data || { eligible: false, reason: "unknown" }) as never;
    },
    async submit(id: string): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("submit_bid_pack", { p_id: id });
      if (error) { console.warn("[bid_packs.submit]", error); return null; }
      lcAudit({ event_type: "bid_pack_submitted", metadata: { pack_id: id } });
      return (data as string) || null;
    },
    async withdraw(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("withdraw_bid_pack", { p_id: id });
      if (error) { console.warn("[bid_packs.withdraw]", error); return false; }
      lcAudit({ event_type: "bid_pack_withdrawn", metadata: { pack_id: id } });
      return true;
    },
    /* v50.34 — Slice B: bid pack documents. Partner uploads priced
       quote / method statement / etc. Mirrors the v50.33 tender-docs
       shape but bucketed by pack id. Originator can download once
       status is submitted+; partner can manage at any status. */
    async uploadDocument(packId: string, file: File, label?: string): Promise<BidPackDocument | null> {
      if (!SUPABASE_READY) return null;
      const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
      const path     = `${packId}/${Date.now()}-${safeName}`;
      try {
        const { error: upErr } = await supabase.storage
          .from("bid-pack-docs")
          .upload(path, file, { upsert: false, cacheControl: "3600", contentType: file.type });
        if (upErr) { console.warn("[bid_packs.uploadDocument] storage:", upErr); return null; }
        const sessionRes = await supabase.auth.getSession();
        const userId = sessionRes?.data?.session?.user?.id || null;
        const { data: row, error: insErr } = await supabase
          .from("bid_pack_documents")
          .insert({
            bid_pack_id:         packId,
            file_path:           path,
            file_name:           file.name,
            file_size:           file.size,
            mime_type:           file.type || null,
            label:               label || null,
            uploaded_by_user_id: userId,
          })
          .select().single();
        if (insErr) {
          /* Best-effort: cleanup the orphan blob since the metadata
             insert failed. */
          try { await supabase.storage.from("bid-pack-docs").remove([path]); } catch { /* ignore */ }
          console.warn("[bid_packs.uploadDocument] insert:", insErr);
          return null;
        }
        lcAudit({
          event_type: "bid_pack_document.uploaded",
          ui_string:  label || file.name,
          metadata:   { document_id: (row as BidPackDocument).id, bid_pack_id: packId, file_size: file.size, mime: file.type || null },
        });
        return row as BidPackDocument;
      } catch (e) {
        console.warn("[bid_packs.uploadDocument] threw:", e);
        return null;
      }
    },
    async listDocuments(packId: string): Promise<BidPackDocument[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("bid_pack_documents")
        .select("*")
        .eq("bid_pack_id", packId)
        .is("deleted_at", null)
        .order("uploaded_at", { ascending: false });
      if (error) { console.warn("[bid_packs.listDocuments]", error); return []; }
      return (data as BidPackDocument[]) || [];
    },
    async documentSignedUrl(filePath: string, ttlSeconds = 3600): Promise<string | null> {
      if (!SUPABASE_READY || !filePath) return null;
      const { data, error } = await supabase.storage.from("bid-pack-docs").createSignedUrl(filePath, ttlSeconds);
      if (error) { console.warn("[bid_packs.documentSignedUrl]", error); return null; }
      return data?.signedUrl || null;
    },
    async deleteDocument(documentId: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase
        .from("bid_pack_documents")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", documentId);
      if (error) { console.warn("[bid_packs.deleteDocument]", error); return false; }
      lcAudit({ event_type: "bid_pack_document.deleted", metadata: { document_id: documentId } });
      return true;
    },
    /* v50.50 — private reviewer notes (originator-side scratch space).
       RLS scopes SELECT to originator company; partners see nothing
       even on their own packs. Empty-string SELECT for a row that
       doesn't exist returns null — caller treats that as "no note". */
    async getReviewerNote(packId: string): Promise<{ content: string; updated_at: string } | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("bid_pack_reviewer_notes")
        .select("content, updated_at")
        .eq("bid_pack_id", packId)
        .maybeSingle();
      if (error) { console.warn("[bid_packs.getReviewerNote]", error); return null; }
      return data || null;
    },
    /* Upsert — single note per pack, last write wins. Trimmed-empty
       content triggers delete instead of an empty row. */
    async saveReviewerNote(packId: string, content: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const trimmed = content.trim();
      if (!trimmed) {
        const { error } = await supabase.from("bid_pack_reviewer_notes").delete().eq("bid_pack_id", packId);
        if (error) { console.warn("[bid_packs.saveReviewerNote] delete-empty:", error); return false; }
        lcAudit({ event_type: "bid_pack.reviewer_note_cleared", metadata: { pack_id: packId } });
        return true;
      }
      const sess = await supabase.auth.getSession();
      const uid  = sess?.data?.session?.user?.id;
      if (!uid) return false;
      const { error } = await supabase
        .from("bid_pack_reviewer_notes")
        .upsert({ bid_pack_id: packId, content: trimmed, author_id: uid }, { onConflict: "bid_pack_id" });
      if (error) { console.warn("[bid_packs.saveReviewerNote]", error); return false; }
      lcAudit({ event_type: "bid_pack.reviewer_note_saved", metadata: { pack_id: packId } });
      return true;
    },
    /* v49.88 — outcome marking by the originator. */
    async markOutcome(id: string, outcome: "shortlisted" | "won" | "lost", reason: string | null): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("mark_bid_outcome", {
        p_id: id, p_outcome: outcome, p_reason: reason,
      });
      if (error) { console.warn("[bid_packs.markOutcome]", error); return false; }
      lcAudit({ event_type: `bid_outcome_${outcome}`, metadata: { pack_id: id } });
      return true;
    },
    async unmarkOutcome(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("unmark_bid_outcome", { p_id: id });
      if (error) { console.warn("[bid_packs.unmarkOutcome]", error); return false; }
      lcAudit({ event_type: "bid_outcome_reverted", metadata: { pack_id: id } });
      return true;
    },
    /* v49.88 — used by the win → origination flow. After the partner
       submits the origination created from a won bid, the wizard updates
       this column so the bid_pack is linked to its origination. RLS
       (partner_all policy) gates the update. */
    async linkOrigination(packId: string, originationId: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase
        .from("bid_packs")
        .update({ origination_id: originationId })
        .eq("id", packId);
      if (error) { console.warn("[bid_packs.linkOrigination]", error); return false; }
      return true;
    },
    /* v49.87 — list bids RECEIVED on an opportunity. RLS already gives
       originator company members read access (v49.85 policy); this is
       just the query helper. */
    async listForOpportunity(opportunityId: string): Promise<Array<BidPack & { partner_co?: { name?: string; partner_progression?: string } | null }>> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("bid_packs")
        .select("*, partner_co:partner_company_id(name, partner_progression, reliability_score)")
        .eq("bid_opportunity_id", opportunityId)
        .order("registered_interest_at", { ascending: true });
      if (error) { console.warn("[bid_packs.listForOpportunity]", error); return []; }
      return (data as never) || [];
    },
    /* v49.86 — auto-attached context for the pack-assembly page. Counts
       compliance docs, accreditations, and the AFR/RIDDOR declared on
       the latest approved onboarding submission. All RLS-gated by
       company membership. */
    async loadAutoAttachedContext(companyId: string): Promise<{
      compliance_doc_count:        number;
      compliance_declared_count:   number;
      insurance_lines:             Array<{ kind: string; cover: number; expiry: string | null }>;
      afr:    number | null;
      riddor: number | null;
      latest_submission_at: string | null;
    }> {
      const empty = {
        compliance_doc_count: 0, compliance_declared_count: 0,
        insurance_lines: [], afr: null, riddor: null, latest_submission_at: null,
      };
      if (!SUPABASE_READY) return empty;
      const [docsR, declR, subR] = await Promise.all([
        supabase.from("compliance_documents").select("id", { count: "exact", head: true })
          .eq("company_id", companyId).is("deleted_at", null).eq("is_declaration_only", false),
        supabase.from("compliance_documents").select("id", { count: "exact", head: true })
          .eq("company_id", companyId).is("deleted_at", null).eq("is_declaration_only", true),
        supabase.from("onboarding_submissions").select("data, reviewed_at")
          .eq("company_id", companyId).eq("status", "approved")
          .order("reviewed_at", { ascending: false }).limit(1).maybeSingle(),
      ]);
      const data = (subR.data?.data || {}) as Record<string, unknown>;
      const ins = (data.insurance as Record<string, { coverLevel?: number; expiry?: string | null }> | undefined) || {};
      const insurance_lines: Array<{ kind: string; cover: number; expiry: string | null }> = [];
      for (const [k, v] of Object.entries(ins)) {
        if (!v || !v.coverLevel) continue;
        insurance_lines.push({ kind: k, cover: Number(v.coverLevel) || 0, expiry: v.expiry || null });
      }
      const tr = (data.trackRecord as { afr?: number; riddor?: number } | undefined) || {};
      return {
        compliance_doc_count:      docsR.count ?? 0,
        compliance_declared_count: declR.count ?? 0,
        insurance_lines,
        afr:    typeof tr.afr    === "number" ? tr.afr    : null,
        riddor: typeof tr.riddor === "number" ? tr.riddor : null,
        latest_submission_at: (subR.data?.reviewed_at as string) || null,
      };
    },
  },

  /* v49.82 — deal clarifications channel. A tracked Q&A on a specific
     placement. RLS gives read to members of either side; writes go
     through 3 SECURITY DEFINER RPCs. */
  deal_clarifications: {
    async listForPlacement(placementId: string): Promise<DealClarification[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("deal_clarifications")
        .select("*")
        .eq("placement_id", placementId)
        .order("created_at", { ascending: false });
      if (error) { console.warn("[deal_clarifications.listForPlacement]", error); return []; }
      return (data as DealClarification[]) || [];
    },
    async raise(placementId: string, subject: string, body: string): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("raise_deal_clarification", {
        p_placement_id: placementId,
        p_subject:      subject,
        p_body:         body,
      });
      if (error) { console.warn("[deal_clarifications.raise]", error); return null; }
      lcAudit({ event_type: "deal_clarification.raised", metadata: { id: data, placement_id: placementId } });
      return (data as string) || null;
    },
    async answer(id: string, answer: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("answer_deal_clarification", {
        p_id: id, p_answer: answer,
      });
      if (error) { console.warn("[deal_clarifications.answer]", error); return false; }
      lcAudit({ event_type: "deal_clarification.answered", metadata: { id } });
      return true;
    },
    async resolve(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("resolve_deal_clarification", { p_id: id });
      if (error) { console.warn("[deal_clarifications.resolve]", error); return false; }
      lcAudit({ event_type: "deal_clarification.resolved", metadata: { id } });
      return true;
    },
  },

  /* v49.97 — pre-bid Q&A on a bid_opportunity. Reads route through the
     list_tender_clarifications RPC so asker identity is masked server
     side unless caller is originator or asker. Writes through raise
     and answer RPCs (originator-only on answers). */
  tender_clarifications: {
    async listForOpportunity(opportunityId: string): Promise<TenderClarification[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase.rpc("list_tender_clarifications", {
        p_opportunity_id: opportunityId,
      });
      if (error) { console.warn("[tender_clarifications.listForOpportunity]", error); return []; }
      return (data as TenderClarification[]) || [];
    },
    async raise(opportunityId: string, subject: string, body: string): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("raise_tender_clarification", {
        p_opportunity_id: opportunityId,
        p_subject:        subject,
        p_body:           body,
      });
      if (error) { console.warn("[tender_clarifications.raise]", error); return null; }
      lcAudit({ event_type: "tender_clarification.raised", metadata: { id: data, opportunity_id: opportunityId } });
      return (data as string) || null;
    },
    async answer(id: string, answer: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("answer_tender_clarification", {
        p_id: id, p_answer: answer,
      });
      if (error) { console.warn("[tender_clarifications.answer]", error); return false; }
      lcAudit({ event_type: "tender_clarification.answered", metadata: { id } });
      return true;
    },
  },

  /* v50.77 — futures bookings. Forward-booked labour commitments
     from coord → partner; RLS gives the coord full CRUD on their
     own bookings and the partner read-only. Pricing carries a 15%
     advance-booking premium (LC_FUTURES_PREMIUM_PCT). Pre-Stripe,
     payment_status is 'simulated'. */
  futures_bookings: {
    async listForCompany(companyId: string): Promise<FuturesBooking[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("futures_bookings")
        .select("*")
        .or(`coordinator_company_id.eq.${companyId},partner_company_id.eq.${companyId}`)
        .is("deleted_at", null)
        .order("start_date", { ascending: true });
      if (error) { console.warn("[futures_bookings.listForCompany]", error); return []; }
      return (data as FuturesBooking[] | null) || [];
    },
    async create(args: {
      projectId:             string;
      coordinatorCompanyId:  string;
      partnerCompanyId:      string;
      trades:                string[];
      headcount:             number;
      startDate:             string;
      endDate:               string;
      weeks:                 number;
      dayRatePence:          number;
      baseValuePence:        number;
      premiumPct:            number;
      premiumValuePence:     number;
      contractValuePence:    number;
    }): Promise<{ ok: true; data: FuturesBooking } | { ok: false; reason: string }> {
      /* v50.97 — return shape changed from FuturesBooking|null to
         a tagged ok/reason union so the wizard can surface the
         specific Supabase error in the toast subtitle instead of
         silently failing with a "check console" hint.
         v50.98 — fetch the auth.uid() inside the helper instead of
         making every caller pass createdByUserId. NOT NULL on
         futures_bookings.created_by_user_id was being violated. */
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const sessionRes = await supabase.auth.getSession();
      const createdBy = sessionRes?.data?.session?.user?.id;
      if (!createdBy) return { ok: false, reason: "Not signed in" };
      const { data, error } = await supabase
        .from("futures_bookings")
        .insert({
          project_id:             args.projectId,
          coordinator_company_id: args.coordinatorCompanyId,
          partner_company_id:     args.partnerCompanyId,
          trades:                 args.trades,
          headcount:              args.headcount,
          start_date:             args.startDate,
          end_date:               args.endDate,
          weeks:                  args.weeks,
          day_rate_pence:         args.dayRatePence,
          base_value_pence:       args.baseValuePence,
          premium_pct:            args.premiumPct,
          premium_value_pence:    args.premiumValuePence,
          contract_value_pence:   args.contractValuePence,
          payment_status:         "simulated",
          created_by_user_id:     createdBy,
        })
        .select("*")
        .maybeSingle();
      if (error) {
        console.warn("[futures_bookings.create]", error);
        /* Concatenate the useful bits — PostgREST messages include
           code, message, hint, details. The hint is often the most
           actionable ("did you mean..."). */
        const reason = [error.message, error.code && `(${error.code})`, error.hint, error.details]
          .filter(Boolean).join(" · ");
        return { ok: false, reason: reason || "Unknown error" };
      }
      if (!data) {
        return { ok: false, reason: "Insert succeeded but no row returned (RLS may block read-back)" };
      }
      lcAudit({ event_type: "futures_booking_created", metadata: { project_id: args.projectId, partner_company_id: args.partnerCompanyId, contract_value_pence: args.contractValuePence } });
      return { ok: true, data: data as FuturesBooking };
    },
    /* v50.84 — cancel via SECURITY DEFINER RPC. The RPC computes the
       forfeit server-side: pending = free, confirmed + outside
       window = free, confirmed + inside window = full premium
       forfeit. Caller can no longer cancel from a confirmed status
       by editing the row directly. */
    async cancel(id: string): Promise<{ ok: true } | { ok: false; reason: string }> {
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const { error } = await supabase.rpc("cancel_futures_booking", { p_id: id });
      if (error) { console.warn("[futures_bookings.cancel]", error); return { ok: false, reason: error.message }; }
      /* Audit handled server-side by cancel_futures_booking (migration-089). */
      return { ok: true };
    },
    /* v50.78 — supplier confirmation. RPC enforces partner-membership
       auth and pending-status precondition. */
    async confirm(id: string): Promise<{ ok: true } | { ok: false; reason: string }> {
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const { error } = await supabase.rpc("confirm_futures_booking", { p_id: id });
      if (error) { console.warn("[futures_bookings.confirm]", error); return { ok: false, reason: error.message }; }
      /* Audit handled server-side by confirm_futures_booking (migration-084). */
      return { ok: true };
    },
    /* v50.78 — supplier decline. Reason required (RPC raises on empty). */
    async decline(id: string, reason: string): Promise<{ ok: true } | { ok: false; reason: string }> {
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const { error } = await supabase.rpc("decline_futures_booking", { p_id: id, p_reason: reason });
      if (error) { console.warn("[futures_bookings.decline]", error); return { ok: false, reason: error.message }; }
      /* Audit handled server-side by decline_futures_booking (migration-084). */
      return { ok: true };
    },
    /* v50.78 — fetch a single booking with joins for the confirm modal
       deep-link. RLS gates visibility (partner co + coord co both
       have read). Returns null when the booking isn't visible. */
    async getById(id: string): Promise<FuturesBooking & {
      project?: { name?: string; client_name?: string | null } | null;
      partner?: { name?: string } | null;
      coordinator?: { name?: string } | null;
    } | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("futures_bookings")
        .select("*, project:project_id(name, client_name), partner:partner_company_id(name), coordinator:coordinator_company_id(name)")
        .eq("id", id)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) { console.warn("[futures_bookings.getById]", error); return null; }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any) || null;
    },
  },

  /* v50.82 — partner unsolicited offers. Inverse of the
     v50.81 NewRequirementModal flow. Partner offers crew against a
     specific project + dates + rate; coord accepts (spawns
     placements at stage='introduced') or declines. */
  partner_offers: {
    async listForCompany(companyId: string): Promise<PartnerOffer[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("partner_offers")
        .select("*")
        .or(`partner_company_id.eq.${companyId},coordinator_company_id.eq.${companyId}`)
        .is("deleted_at", null)
        .order("start_date", { ascending: true });
      if (error) { console.warn("[partner_offers.listForCompany]", error); return []; }
      return (data as PartnerOffer[] | null) || [];
    },
    async create(args: {
      partnerCompanyId: string;
      projectId:        string | number;
      trade:            string;
      headcount:        number;
      operativeIds:     string[];
      startDate:        string;
      durationWeeks:    number;
      dayRatePence:     number;
      notes?:           string;
    }): Promise<{ ok: true; id: string } | { ok: false; reason: string }> {
      /* v50.99 — tagged ok/reason union mirroring v50.97 booking
         pattern, so the wizard can show the Postgres error inline. */
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const { data, error } = await supabase.rpc("create_partner_offer", {
        p_partner_company_id: args.partnerCompanyId,
        p_project_id:         args.projectId,
        p_trade:              args.trade,
        p_headcount:          args.headcount,
        p_operative_ids:      args.operativeIds,
        p_start_date:         args.startDate,
        p_duration_weeks:     args.durationWeeks,
        p_day_rate_pence:     args.dayRatePence,
        p_notes:              args.notes ?? null,
      });
      if (error) {
        console.warn("[partner_offers.create]", error);
        const reason = [error.message, error.code && `(${error.code})`, error.hint, error.details]
          .filter(Boolean).join(" · ");
        return { ok: false, reason: reason || "Unknown error" };
      }
      lcAudit({ event_type: "partner_offer_created", metadata: { project_id: args.projectId } });
      const id = (data as string) || "";
      if (!id) return { ok: false, reason: "RPC returned no id" };
      return { ok: true, id };
    },
    async accept(id: string): Promise<{ ok: true } | { ok: false; reason: string }> {
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const { error } = await supabase.rpc("accept_partner_offer", { p_id: id });
      if (error) { console.warn("[partner_offers.accept]", error); return { ok: false, reason: error.message }; }
      /* Audit handled server-side by accept_partner_offer (migration-087). */
      return { ok: true };
    },
    async decline(id: string, reason: string): Promise<{ ok: true } | { ok: false; reason: string }> {
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const { error } = await supabase.rpc("decline_partner_offer", { p_id: id, p_reason: reason });
      if (error) { console.warn("[partner_offers.decline]", error); return { ok: false, reason: error.message }; }
      /* Audit handled server-side by decline_partner_offer (migration-087). */
      return { ok: true };
    },
    async withdraw(id: string): Promise<{ ok: true } | { ok: false; reason: string }> {
      if (!SUPABASE_READY) return { ok: false, reason: "supabase_not_ready" };
      const { error } = await supabase.rpc("withdraw_partner_offer", { p_id: id });
      if (error) { console.warn("[partner_offers.withdraw]", error); return { ok: false, reason: error.message }; }
      /* Audit handled server-side by withdraw_partner_offer (migration-087). */
      return { ok: true };
    },
  },

  /* v49.67 — partner-originated packages. Move 1 of the 2026-05-19
     build instructions. The eligibility RPC enforces the Silver+ /
     5-placements gate; the fee-compute RPC mirrors the v48 PACKAGE_FEE
     schedule; submit_partner_origination is the atomic finalize. */
  partner_originations: {
    async eligibility(companyId: string): Promise<{ eligible: boolean; reason?: string; progression?: string; completed_placements?: number }> {
      if (!SUPABASE_READY) return { eligible: false, reason: "supabase_not_ready" };
      const { data, error } = await supabase.rpc("partner_origination_eligibility", { p_company_id: companyId });
      if (error) { console.warn("[partner_originations.eligibility]", error); return { eligible: false, reason: "rpc_error" }; }
      return (data || { eligible: false, reason: "unknown" }) as never;
    },
    async computeFee(args: { companyId: string; declaredLabourPence: number; totalHeads: number }): Promise<{ fee_pence: number; base_pct: number; waived: boolean; tier: string } | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("compute_partner_origination_fee", {
        p_company_id:            args.companyId,
        p_declared_labour_pence: args.declaredLabourPence,
        p_total_heads:           args.totalHeads,
      });
      if (error) { console.warn("[partner_originations.computeFee]", error); return null; }
      return (data || null) as never;
    },
    async createDraft(args: { partnerCompanyId: string; projectName: string }): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase
        .from("partner_originations")
        .insert({
          partner_company_id: args.partnerCompanyId,
          project_name:       args.projectName,
          status:             "draft",
        })
        .select("id")
        .single();
      if (error) { console.warn("[partner_originations.createDraft]", error); return null; }
      lcAudit({ event_type: "partner_origination_started", metadata: { id: (data as { id: string }).id } });
      return (data as { id: string }).id;
    },
    async updateDraft(id: string, patch: Partial<PartnerOrigination>): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.from("partner_originations").update(patch).eq("id", id);
      if (error) { console.warn("[partner_originations.updateDraft]", error); return false; }
      return true;
    },
    async loadDraft(id: string): Promise<PartnerOrigination | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.from("partner_originations").select("*").eq("id", id).maybeSingle();
      if (error) { console.warn("[partner_originations.loadDraft]", error); return null; }
      return (data as PartnerOrigination) || null;
    },
    async submit(id: string): Promise<string | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("submit_partner_origination", { p_id: id });
      if (error) { console.warn("[partner_originations.submit]", error); return null; }
      lcAudit({ event_type: "partner_origination_submitted", ui_string: String(data || ""), metadata: { id } });
      return (data as string) || null;
    },
    async revise(id: string, newDeclaredLabourPence: number): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      /* Per §1.5: surface revision in audit log with old/new/delta. */
      const { data: cur } = await supabase
        .from("partner_originations")
        .select("declared_labour_value_pence, partner_company_id, total_heads")
        .eq("id", id)
        .maybeSingle();
      const oldValue = (cur as { declared_labour_value_pence?: number } | null)?.declared_labour_value_pence || 0;
      const companyId = (cur as { partner_company_id?: string } | null)?.partner_company_id;
      const totalHeads = (cur as { total_heads?: number } | null)?.total_heads || 0;
      if (!companyId) return false;
      const calc = await this.computeFee({ companyId, declaredLabourPence: newDeclaredLabourPence, totalHeads });
      const { error } = await supabase
        .from("partner_originations")
        .update({
          declared_labour_value_pence: newDeclaredLabourPence,
          computed_fee_pence: calc?.fee_pence ?? null,
          base_fee_pct:       calc?.base_pct  ?? null,
          status:             "revised",
        })
        .eq("id", id);
      if (error) { console.warn("[partner_originations.revise]", error); return false; }
      lcAudit({
        event_type: "partner_origination_revised",
        metadata:   { id, oldValue, newValue: newDeclaredLabourPence, delta: newDeclaredLabourPence - oldValue },
      });
      return true;
    },
    async cancel(id: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.from("partner_originations").update({ status: "cancelled" }).eq("id", id);
      if (error) { console.warn("[partner_originations.cancel]", error); return false; }
      lcAudit({ event_type: "partner_origination_cancelled", metadata: { id } });
      return true;
    },
    async listForCompany(companyId: string): Promise<PartnerOrigination[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase
        .from("partner_originations")
        .select("*")
        .eq("partner_company_id", companyId)
        .order("created_at", { ascending: false });
      if (error) { console.warn("[partner_originations.listForCompany]", error); return []; }
      return (data as PartnerOrigination[]) || [];
    },
    /* v49.77 — admin queue surface. Reads partner_originations; row
       visibility is gated by the partner_orig_admin_read RLS policy. */
    async adminListAll(args: { status?: string; limit?: number } = {}): Promise<PartnerOrigination[]> {
      if (!SUPABASE_READY) return [];
      let q = supabase
        .from("partner_originations")
        .select("*")
        .order("submitted_at", { ascending: true, nullsFirst: false })
        .limit(args.limit ?? 200);
      if (args.status) q = q.eq("status", args.status);
      const { data, error } = await q;
      if (error) { console.warn("[partner_originations.adminListAll]", error); return []; }
      return (data as PartnerOrigination[]) || [];
    },
    async adminApprove(id: string, notes: string | null): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("admin_approve_partner_origination", {
        p_id: id, p_notes: notes,
      });
      if (error) { console.warn("[partner_originations.adminApprove]", error); return false; }
      lcAudit({ event_type: "partner_origination.admin_approved", metadata: { id } });
      return true;
    },
    async adminReject(id: string, reason: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("admin_reject_partner_origination", {
        p_id: id, p_reason: reason,
      });
      if (error) { console.warn("[partner_originations.adminReject]", error); return false; }
      lcAudit({ event_type: "partner_origination.admin_rejected", metadata: { id } });
      return true;
    },
    /* v49.78 — "more info needed" middle action. Flips submitted/revised
       to 'more_info_needed' with required gaps text. Applicant gets an
       email + "Resume editing" CTA on /me?tab=originations. */
    async adminRequestMoreInfo(id: string, gaps: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("admin_request_more_info_partner_origination", {
        p_id: id, p_gaps: gaps,
      });
      if (error) { console.warn("[partner_originations.adminRequestMoreInfo]", error); return false; }
      lcAudit({ event_type: "partner_origination.admin_more_info_requested", metadata: { id } });
      return true;
    },
    /* v49.68 — distinct client names this partner has registered wins
       against. Used by the coordinator visibility badge. */
    async clientNames(partnerCompanyId: string): Promise<string[]> {
      if (!SUPABASE_READY) return [];
      const { data, error } = await supabase.rpc("partner_clients_list", { p_partner_company_id: partnerCompanyId });
      if (error) { console.warn("[partner_originations.clientNames]", error); return []; }
      return (data as string[]) || [];
    },
    /* v49.68 — day-rate percentile backing the anti-gaming guardrail. */
    async dayRatePercentile(trade: string, percentile = 0.10): Promise<number | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("placement_day_rate_percentile", {
        p_trade: trade, p_percentile: percentile,
      });
      if (error) { console.warn("[partner_originations.dayRatePercentile]", error); return null; }
      return typeof data === "number" ? data : (data ? Number(data) : null);
    },
  },

  profiles:             Object.assign(table<Profile>("profiles"), {
    async dismissOnboarding() {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("dismiss_onboarding");
      if (error) { console.warn("[LABOURCORE_API] profiles.dismissOnboarding:", error); return false; }
      lcAudit({ event_type: "onboarding.dismissed" });
      return true;
    },
    /* v49.53 — GDPR Article 17 self-serve deletion. Caller must pass
       their own email as the confirmation string; server compares
       case-insensitively against auth.users.email and refuses if not
       matched. On success the profile is anonymised, memberships
       soft-deleted, sole-member companies + operatives PII-cleared and
       soft-deleted. Returns true on success, false on RPC error. */
    async requestDeletion(confirmEmail: string): Promise<boolean> {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.rpc("request_account_deletion", {
        p_confirmation_email: confirmEmail,
      });
      if (error) { console.warn("[LABOURCORE_API] profiles.requestDeletion:", error); return false; }
      lcAudit({ event_type: "account.deletion_requested", ui_string: "Self-serve account deletion" });
      return true;
    },
    /* v49.51 — GDPR Article 20 data export. Returns the whole tree as
       a single jsonb. Caller is expected to download it as a file. */
    async exportMyData(): Promise<Record<string, unknown> | null> {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.rpc("export_my_data");
      if (error) { console.warn("[LABOURCORE_API] profiles.exportMyData:", error); return null; }
      const doc = (data || null) as Record<string, unknown> | null;
      if (doc) {
        const sectionCount = Object.keys(doc).length;
        lcAudit({
          event_type: "data_export.downloaded",
          ui_string:  `Exported ${sectionCount} sections`,
          metadata:   { sections: Object.keys(doc) },
        });
      }
      return doc;
    },
  }),
  operatives:           Object.assign(table<Operative>("operatives"), {
    async findById(id: string) {
      if (!SUPABASE_READY || !id) return null;
      const { data, error } = await supabase
        .from("operatives")
        .select("*, company:company_id(id,name,kind,partner_progression,reliability_score)")
        .eq("id", id)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) { console.warn("[LABOURCORE_API] operatives.findById:", error); return null; }
      return data;
    },
    async placementsForOperative(id: string) {
      if (!SUPABASE_READY || !id) return null;
      const { data, error } = await supabase
        .from("placements")
        .select("id, stage, agreed_day_rate, agreed_at, onsite_at, completed_at, created_at, requirement_id, operative_id, partner_company_id, coordinator_company_id, requirement:requirement_id(trade,headcount,start_date,location), partner_co:partner_company_id(name), coord_co:coordinator_company_id(name)")
        .eq("operative_id", id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) { console.warn("[LABOURCORE_API] operatives.placementsForOperative:", error); return null; }
      return data || [];
    },
    async ratingsForOperative(id: string) {
      if (!SUPABASE_READY || !id) return null;
      /* Ratings live on placement_ratings keyed by placement. Pull the
         placements first then ratings for that set. RLS will gate. */
      const { data: pls } = await supabase
        .from("placements")
        .select("id")
        .eq("operative_id", id)
        .is("deleted_at", null);
      const ids = (pls || []).map(p => p.id);
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from("placement_ratings")
        .select("id, placement_id, reliability, quality, comms, comment, rater_side, rated_company_id, created_at")
        .in("placement_id", ids)
        .order("created_at", { ascending: false });
      if (error) { console.warn("[LABOURCORE_API] operatives.ratingsForOperative:", error); return null; }
      return data || [];
    },
  }),
  requirements,
  placements,
  placement_ratings,
  daily_logs,
  coord_crew_templates,
  fee_events,
  platform_invoices,
  compliance_documents,
  placement_messages,
  placement_notifications,
  partner_onboarding,
  compliance_requests,
  disputes,
  dispute_comments,
  placement_milestones,
  invoices,
  /* Insights composite namespace. */
  insights,
  /* Audit. */
  audit,
  /* Legacy tables — still in active use through Phase 5 migration window. */
  workers:              table("workers"),
  projects:             table("projects"),
  deals:                table("deals"),
  invitations:          table("invitations"),
  messages:             table("messages"),
  ratings:              table("ratings"),
  approved_workers:     table("approved_workers"),
  discontinued_workers: table("discontinued_workers"),
  appeals:              table("appeals"),
  notifications:        table("notifications"),
  mailbox_messages:     table("mailbox_messages"),
  user_notifications:   table("user_notifications"),
  gdpr_consents:        table("gdpr_consents"),
  contract_signatures:  table("contract_signatures"),
  contract_offers,
  deal_milestones:      table("deal_milestones"),
};

/* Re-expose on window so v48.x console diagnostics / lcClearEncryptedTables
   / shellSignOut and the legacy IIFE-scoped helpers can still find it
   during the migration. Removed at v49.8 cutover. */
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).LABOURCORE_API = LABOURCORE_API;
}

export default LABOURCORE_API;
export * from "./types";
